import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import { Button, Card, Input, Select, Spinner, Badge } from "../components/ui";
import { fmtDateTime } from "../lib/format";
import { ColumnFilter } from "../components/ColumnFilter";
import { MainCatTab } from "../components/MainCatTab";
import { AttrMapTab } from "../components/AttrMapTab";
import { childIndex, parentIndex, toggleSelection, topmostSelected, partiallySelected } from "@shared/treeSelect";

interface Scalar { key: string; value: unknown; valueType: string; category: string; label: string | null; description: string | null }

/** Same reversal the server applies (server/domain/exactItem.ts) — here only to show what a rule does. */
const revCode = (s: string) => s.split("").reverse().join("");
/** A real-shaped PBS code, used purely as the worked example for the brand rules. */
const SAMPLE_PBS_CODE = "001345Z";
const itemCodeExample = (prefix: string, transform: "identity" | "reverse") =>
  `${prefix}${transform === "reverse" ? revCode(SAMPLE_PBS_CODE) : SAMPLE_PBS_CODE}`;
type ExclSystem = "lightspeed" | "exact" | "pbs";
type Tab = "general" | "vat" | "units" | "brands" | "excluded" | "mapping" | "exactsrc" | "pbssrc" | "lssrc" | "schedules" | "users" | "eco-rules" | "main-cat" | "attr-map";

// Scalars whose value is a fixed set of choices → render a dropdown, not a free-text box.
const ENUM_SCALARS: Record<string, string[]> = {
  "exact.fallbackTransform": ["reverse", "identity"],
};

function ScalarRow({ s, onSave, saving }: { s: Scalar; onSave: (key: string, value: unknown) => void; saving?: boolean }) {
  const [val, setVal] = useState<string>(s.valueType === "boolean" ? String(Boolean(s.value)) : String(s.value ?? ""));
  const [justSaved, setJustSaved] = useState(false);
  const dirty = val !== (s.valueType === "boolean" ? String(Boolean(s.value)) : String(s.value ?? ""));
  const enumOpts = ENUM_SCALARS[s.key];
  const save = () => {
    let v: unknown = val;
    if (s.valueType === "number") v = Number(val);
    else if (s.valueType === "boolean") v = val === "true";
    else if (s.valueType === "json") { try { v = JSON.parse(val); } catch { /* keep string */ } }
    onSave(s.key, v);
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2500);
  };
  return (
    <tr>
      <td className="py-2"><div className="font-medium text-slate-800">{s.label ?? s.key}</div><div className="text-xs text-slate-400">{s.key}</div></td>
      <td className="text-xs text-slate-500">{s.description}</td>
      <td>
        {enumOpts ? (
          <Select value={val} onChange={(e) => setVal(e.target.value)}>{enumOpts.map((o) => <option key={o} value={o}>{o}</option>)}</Select>
        ) : s.valueType === "boolean" ? (
          <Select value={val} onChange={(e) => setVal(e.target.value)}><option value="true">true</option><option value="false">false</option></Select>
        ) : (
          <Input value={val} onChange={(e) => setVal(e.target.value)} className="w-48" />
        )}
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Button variant={dirty ? "primary" : "secondary"} onClick={save} disabled={saving}>Save</Button>
          {justSaved && <span className="text-xs font-medium text-emerald-600">saved ✓</span>}
        </div>
      </td>
    </tr>
  );
}

/**
 * Search-as-you-type category picker for the excluded list. exact/pbs search the imported PBS category
 * tree by NAME or id (so you don't need to know the id); lightspeed has no local cache, so it accepts a
 * raw id and the name is resolved on add. Picking a result adds it immediately (with its name as label).
 */
function CategoryPicker({ system, value, onChange, onPick }: { system: ExclSystem; value: string; onChange: (v: string) => void; onPick: (categoryId: string, label: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const searchable = system !== "lightspeed";
  const results = trpc.config.categorySearch.useQuery({ system, q: value, limit: 20 }, { enabled: searchable && open && value.trim().length >= 1 });
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={searchable ? "search category name or id…" : "Lightspeed category id"}
        className="w-72"
      />
      {searchable && open && value.trim() && (
        <div className="absolute z-20 mt-1 max-h-64 w-96 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {results.isLoading && <div className="px-3 py-2 text-xs text-slate-400">searching…</div>}
          {!results.isLoading && (results.data?.length ?? 0) === 0 && <div className="px-3 py-2 text-xs text-slate-400">no matching categories</div>}
          {results.data?.map((c) => (
            <button
              key={c.categoryId}
              onMouseDown={(e) => { e.preventDefault(); onPick(c.categoryId, c.name ?? null); setOpen(false); }}
              className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-indigo-50"
            >
              <span className="truncate text-slate-700">{c.name ?? "(no name)"}</span>
              <span className="ml-2 shrink-0 font-mono text-[11px] text-slate-400">{c.categoryId}{c.level ? ` · L${c.level}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Checkbox that can also show the third state — some, but not all, of the branch is selected. */
function TriCheckbox({ checked, indeterminate, onChange, label }: { checked: boolean; indeterminate: boolean; onChange: () => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && indeterminate; // DOM-only property — React can't set it via props
  }, [checked, indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} className="accent-indigo-600" aria-label={label} />;
}

/**
 * Tree picker for PBS category exclusions (system "pbs" — the source-level exclusion: a category ticked
 * here is skipped everywhere, for ExactOnline as well as Lightspeed). Renders the imported PBS tree with
 * a rolled-up product count per branch. Ticking a PARENT is enough — the reconcile walks a product's
 * category up the tree — so children are shown as "via parent" rather than needing their own row ticked.
 * Search keeps the matching branches (and their ancestors) visible and open.
 */
function PbsCategoryTreePicker({ onFlash }: { onFlash: (msg: string, ok?: boolean) => void }) {
  const utils = trpc.useUtils();
  const tree = trpc.config.excluded.pbsTree.useQuery(undefined, { staleTime: 60_000 });
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<Set<string>>(new Set());

  const bulk = trpc.config.excluded.bulkSet.useMutation({
    onSuccess: (r) => {
      utils.config.get.invalidate();
      utils.config.excluded.pbsTree.invalidate();
      const n = r.added || r.removed;
      const verb = r.added ? "excluded" : "un-excluded";
      setSel(new Set());
      onFlash(`${n} PBS categor${n === 1 ? "y" : "ies"} ${verb} ✓`);
    },
    onError: (e: { message: string }) => onFlash(`Save failed: ${e.message}`, false),
  });

  const nodes = tree.data ?? [];

  const { byId, childrenOf, childIds, parentOf, roots, totalOf, pathOf } = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.categoryId, n] as const));
    // Plain id→id indexes for the (pure, tested) selection maths.
    const childIds = childIndex(nodes);
    const parentOf = parentIndex(nodes);
    const childrenOf = new Map<string, typeof nodes>();
    const roots: typeof nodes = [];
    for (const n of nodes) {
      // A parent id that isn't in the tree (partial import) would hide the whole branch — treat it as a root.
      if (n.parentId && byId.has(n.parentId)) {
        const list = childrenOf.get(n.parentId) ?? [];
        list.push(n);
        childrenOf.set(n.parentId, list);
      } else {
        roots.push(n);
      }
    }
    // Products sitting anywhere beneath a node (post-order, so each node is computed once).
    const totalOf = new Map<string, number>();
    const total = (id: string): number => {
      const cached = totalOf.get(id);
      if (cached != null) return cached;
      totalOf.set(id, 0); // cycle guard: a self-referencing parent must not recurse forever
      const own = byId.get(id)?.products ?? 0;
      const sum = (childrenOf.get(id) ?? []).reduce((acc, c) => acc + total(c.categoryId), own);
      totalOf.set(id, sum);
      return sum;
    };
    for (const n of nodes) total(n.categoryId);
    // "Root › … › Leaf" per node, so search can match a full breadcrumb the way the Mapping page does.
    const pathOf = new Map<string, string>();
    const path = (id: string): string => {
      const cached = pathOf.get(id);
      if (cached != null) return cached;
      pathOf.set(id, "");
      const n = byId.get(id);
      const name = n?.name ?? id;
      const parent = n?.parentId && byId.has(n.parentId) ? `${path(n.parentId)} › ` : "";
      const full = `${parent}${name}`;
      pathOf.set(id, full);
      return full;
    };
    for (const n of nodes) path(n.categoryId);
    return { byId, childrenOf, childIds, parentOf, roots, totalOf, pathOf };
  }, [nodes]);

  /** Is this category already excluded because an ANCESTOR is excluded (system "pbs")? */
  const excludedViaParent = (id: string): boolean => {
    let cur = byId.get(id)?.parentId ?? null;
    for (let hops = 0; cur && hops < 10; hops++) {
      const n = byId.get(cur);
      if (!n) break;
      if (n.excluded.includes("pbs")) return true;
      cur = n.parentId ?? null;
    }
    return false;
  };

  // Search: every term must appear in the id/name/breadcrumb (same AND-match as the Mapping search).
  // Matches AND their ancestors stay visible, and the ancestors auto-open so hits aren't buried.
  const { visible, autoOpen } = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/[\s›>/]+/).filter(Boolean);
    if (!terms.length) return { visible: null as Set<string> | null, autoOpen: new Set<string>() };
    const visible = new Set<string>();
    const autoOpen = new Set<string>();
    for (const n of nodes) {
      const hay = `${n.categoryId} ${n.name ?? ""} ${pathOf.get(n.categoryId) ?? ""}`.toLowerCase();
      if (!terms.every((t) => hay.includes(t))) continue;
      visible.add(n.categoryId);
      let cur = n.parentId ?? null;
      for (let hops = 0; cur && hops < 10; hops++) {
        visible.add(cur);
        autoOpen.add(cur);
        cur = byId.get(cur)?.parentId ?? null;
      }
    }
    return { visible, autoOpen };
  }, [q, nodes, byId, pathOf]);

  const isOpen = (id: string) => expanded.has(id) || autoOpen.has(id);
  const toggleOpen = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };
  // Selection maths (tick a branch → its subtree; untick → subtree + ancestors) lives in shared/treeSelect.ts
  // so it can be unit-tested; this component only holds the state.
  const toggleSel = (id: string) => setSel(toggleSelection(sel, id, childIds, parentOf));
  const partial = useMemo(() => partiallySelected(sel, parentOf), [sel, parentOf]);

  const ROW_CAP = 600; // keep the DOM light; searching narrows it down further
  const rows: { node: (typeof nodes)[number]; depth: number }[] = [];
  const collect = (list: typeof nodes, depth: number) => {
    for (const n of [...list].sort((a, b) => (a.name ?? a.categoryId).localeCompare(b.name ?? b.categoryId, "nl"))) {
      if (visible && !visible.has(n.categoryId)) continue;
      if (rows.length >= ROW_CAP) return;
      rows.push({ node: n, depth });
      const kids = childrenOf.get(n.categoryId) ?? [];
      if (kids.length && isOpen(n.categoryId)) collect(kids, depth + 1);
    }
  };
  collect(roots, 0);
  const truncated = rows.length >= ROW_CAP;

  const selArr = [...sel];
  // Only the TOPMOST ticked node of a branch needs a row: the reconcile walks a product's category up
  // the tree, so its ticked children are already covered and writing them too would add dead rows.
  const selRoots = topmostSelected(sel, parentOf);
  const toExclude = selRoots.filter((id) => !byId.get(id)?.excluded.includes("pbs"));
  // Un-excluding works on the rows that actually exist, at whatever level they sit.
  const selExcluded = selArr.filter((id) => byId.get(id)?.excluded.includes("pbs"));
  const selProducts = selRoots.reduce((acc, id) => acc + (totalOf.get(id) ?? 0), 0);

  if (tree.isLoading) return <div className="py-4"><Spinner /></div>;
  if (!nodes.length) return <p className="py-2 text-sm text-slate-400">No PBS categories imported yet — run a PBS sync first (Data Sync).</p>;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search category, id or path…" className="w-72" />
        {q && <Button size="sm" variant="secondary" onClick={() => setQ("")}>clear</Button>}
        <span className="text-[11px] text-slate-400">{nodes.length.toLocaleString()} categories</span>
      </div>

      {sel.size > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
          <span className="text-sm font-medium text-indigo-900">
            {sel.size.toLocaleString()} selected
            {selRoots.length !== sel.size && <span className="font-normal"> ({selRoots.length} branch{selRoots.length === 1 ? "" : "es"})</span>}
            {" · "}{selProducts.toLocaleString()} product{selProducts === 1 ? "" : "s"} beneath them
          </span>
          <Button
            size="sm"
            disabled={!toExclude.length || bulk.isPending}
            onClick={() => {
              const covers = sel.size > toExclude.length ? ` (covering ${sel.size.toLocaleString()} categories in total)` : "";
              if (confirm(`Exclude ${toExclude.length} PBS categor${toExclude.length === 1 ? "y" : "ies"}${covers}? Their products are skipped in BOTH syncs (ExactOnline and Lightspeed) from the next reconcile on.`)) {
                bulk.mutate({ system: "pbs", categoryIds: toExclude, exclude: true });
              }
            }}
            title="Only the topmost ticked category of each branch gets a row — everything beneath it is covered automatically"
          >
            {bulk.isPending ? "Saving…" : `Exclude ${toExclude.length || ""}`.trim()}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!selExcluded.length || bulk.isPending}
            onClick={() => {
              if (confirm(`Remove the exclusion from ${selExcluded.length} PBS categor${selExcluded.length === 1 ? "y" : "ies"}?`)) {
                bulk.mutate({ system: "pbs", categoryIds: selExcluded, exclude: false });
              }
            }}
          >
            {`Un-exclude ${selExcluded.length || ""}`.trim()}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setSel(new Set())}>clear selection</Button>
        </div>
      )}

      <div className="max-h-[28rem] overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="w-8 py-2"></th><th className="py-2">Category</th><th className="w-24">ID</th><th className="w-28 text-right pr-3">Products</th><th className="w-32">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(({ node, depth }) => {
              const kids = childrenOf.get(node.categoryId) ?? [];
              const direct = node.excluded.includes("pbs");
              const viaParent = !direct && excludedViaParent(node.categoryId);
              return (
                <tr key={node.categoryId} className={direct ? "bg-amber-50/60" : undefined}>
                  <td className="py-1.5 pl-2">
                    <TriCheckbox
                      checked={sel.has(node.categoryId)}
                      indeterminate={partial.has(node.categoryId)}
                      onChange={() => toggleSel(node.categoryId)}
                      label={`select ${node.name ?? node.categoryId}`}
                    />
                  </td>
                  <td className="py-1.5">
                    <div className="flex items-center" style={{ paddingLeft: `${depth * 16}px` }}>
                      {kids.length ? (
                        <button onClick={() => toggleOpen(node.categoryId)} className="mr-1 w-4 shrink-0 text-slate-400 hover:text-slate-700" aria-label={isOpen(node.categoryId) ? "collapse" : "expand"}>
                          {isOpen(node.categoryId) ? "▾" : "▸"}
                        </button>
                      ) : (
                        <span className="mr-1 w-4 shrink-0" />
                      )}
                      <span className={viaParent ? "text-slate-400" : "text-slate-800"}>{node.name ?? "(no name)"}</span>
                      {kids.length > 0 && <span className="ml-2 text-[11px] text-slate-400">({kids.length})</span>}
                    </div>
                  </td>
                  <td className="font-mono text-xs text-slate-500">{node.categoryId}</td>
                  <td className="pr-3 text-right tabular-nums text-slate-600">{(totalOf.get(node.categoryId) ?? 0).toLocaleString()}</td>
                  <td className="pr-2">
                    {direct ? <Badge color="gray">excluded</Badge> : viaParent ? <span className="text-[11px] text-slate-400">via parent</span> : node.excluded.includes("exact") ? <Badge color="green">exact only</Badge> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {truncated && <p className="mt-1 text-[11px] text-amber-700">Showing the first {ROW_CAP} rows — narrow it down with the search box.</p>}
      <p className="mt-1 text-[11px] text-slate-400">
        Ticking a branch ticks everything under it; untick the ones you want to keep and the parent drops to half-ticked (only the categories still ticked are excluded).
        Each branch is stored as ONE row on its topmost ticked category — everything beneath it is covered automatically. The count is every product beneath the category.
        Takes effect on the next reconcile — already-created items are not removed.
      </p>
    </div>
  );
}

const TABS: { id: Tab; label: string }[] = [
  { id: "general", label: "General" },
  { id: "vat", label: "VAT codes" },
  { id: "units", label: "STUK units" },
  { id: "brands", label: "Brand rules" },
  { id: "excluded", label: "Exclusions" },
  { id: "mapping", label: "Mapping" },
  { id: "exactsrc", label: "ExactOnline data" },
  { id: "pbssrc", label: "PBS data" },
  { id: "lssrc", label: "Lightspeed" },
  { id: "schedules", label: "Schedules" },
  { id: "users", label: "Users" },
  { id: "eco-rules", label: "Eco rules" },
  { id: "main-cat", label: "Main Cat" },
  { id: "attr-map", label: "Attributen" },
];

/** Search-as-you-type picker over the cached Lightspeed categories or brands. The query can be
 *  controlled (so the caller can reuse the typed text, e.g. as a new-category name). */
function LsPicker({ kind, onPick, query, onQueryChange, placeholder, pathPrefix }: { kind: "category" | "brand"; onPick: (id: number, title: string, path?: string) => void; query?: string; onQueryChange?: (v: string) => void; placeholder?: string; pathPrefix?: string }) {
  const [iq, setIq] = useState("");
  const q = query ?? iq;
  const setQ = (v: string) => (onQueryChange ? onQueryChange(v) : setIq(v));
  const [open, setOpen] = useState(false);
  const catQ = trpc.config.lsCategorySearch.useQuery({ q, limit: 20, pathPrefix }, { enabled: kind === "category" && open });
  const brandQ = trpc.config.lsBrandSearch.useQuery({ q, limit: 20 }, { enabled: kind === "brand" && open });
  const results = ((kind === "category" ? catQ.data : brandQ.data) ?? []) as { id: number; title: string; path?: string }[];
  return (
    <div className="relative inline-block">
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder ?? `pick LS ${kind}…`}
        className="w-56"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">{q ? "no matches" : "type to search…"}</div>}
          {results.map((r) => (
            <button
              key={r.id}
              onMouseDown={(e) => { e.preventDefault(); onPick(r.id, r.title, r.path); setOpen(false); setQ(""); }}
              className="flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-indigo-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-slate-700">{r.title}</span>
                {r.path && r.path !== r.title && <span className="block truncate text-[11px] text-slate-400" title={r.path}>{r.path}</span>}
              </span>
              <span className="ml-2 shrink-0 font-mono text-[11px] text-slate-400">#{r.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One row's mapping cell. A mapped row STAYS in place and shows "✓ <LS name>" with inline
 * change / remove (no need to toggle a filter to find it again); an unmapped row shows the
 * suggestion + picker (+ optional "create in LS"). "change" reveals the picker in place.
 */
function MapCell({
  kind,
  mapped,
  suggestion,
  mappingScore,
  defaultName,
  busy,
  onPick,
  onRemove,
  onCreate,
}: {
  kind: "category" | "brand";
  mapped: { lsId: number; lsTitle: string; lsPath?: string | null } | null;
  suggestion: { lsId: number; lsTitle: string; lsPath?: string; score?: number } | null;
  mappingScore?: number | null;
  defaultName?: string; // fallback name when creating a new LS category (the PBS category name)
  busy?: boolean; // a create-in-LS for THIS row is in flight (show progress, keep the form open)
  onPick: (lsId: number, lsTitle: string, lsPath?: string) => void;
  onRemove: () => void;
  onCreate?: (title: string, parentLsId: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(""); // the picker's typed text — reused as the new-category name when creating
  const [creating, setCreating] = useState(false);
  const [parent, setParent] = useState<{ lsId: number; lsTitle: string } | null>(null);
  const betterSuggestion = mapped && suggestion && typeof mappingScore === "number" && suggestion.score != null && suggestion.score > mappingScore + 15 && suggestion.lsId !== mapped.lsId ? suggestion : null;
  if (mapped && !editing) {
    const scoreColor = typeof mappingScore === "number"
      ? mappingScore >= 70 ? "text-emerald-700 bg-emerald-50" : mappingScore >= 40 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50"
      : null;
    return (
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5">
            <Badge color="green">✓ {mapped.lsTitle}</Badge>
            {scoreColor && <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${scoreColor}`}>{mappingScore}%</span>}
            <span className="font-mono text-[11px] text-slate-400">#{mapped.lsId}</span>
          </span>
          <button onClick={() => setEditing(true)} className="text-xs font-medium text-indigo-600 hover:underline">change</button>
          <button onClick={onRemove} className="text-xs font-medium text-red-600 hover:underline">remove</button>
        </div>
        {mapped.lsPath && mapped.lsPath !== mapped.lsTitle && (
          <div className="text-[11px] text-slate-400 pl-0.5">{mapped.lsPath}</div>
        )}
        {betterSuggestion && (
          <button
            onClick={() => { onPick(betterSuggestion.lsId, betterSuggestion.lsTitle); }}
            className="mt-0.5 flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
            title={`Better path match: ${betterSuggestion.lsTitle} (${betterSuggestion.score}% vs current ${mappingScore}%)`}
          >
            ⚡ Better: {betterSuggestion.lsTitle} <span className="font-normal text-indigo-500">({betterSuggestion.score}%)</span>
          </button>
        )}
      </div>
    );
  }
  const newName = q.trim() || defaultName || "";
  return (
    <div className="space-y-1.5">
      {busy && !creating ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600"><span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500" />Mapping…</span>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {suggestion && (
            <button
              onClick={() => { onPick(suggestion.lsId, suggestion.lsTitle); setEditing(false); }}
              className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-left leading-tight hover:bg-emerald-100"
              title="Use this suggested Lightspeed category"
            >
              <span className="block text-xs font-medium text-emerald-800">use “{suggestion.lsTitle}”{typeof suggestion.score === "number" && <span className="ml-1 font-normal text-emerald-600">· {suggestion.score}% match</span>}</span>
              {suggestion.lsPath && suggestion.lsPath !== suggestion.lsTitle && <span className="block text-[11px] text-emerald-600">{suggestion.lsPath}</span>}
            </button>
          )}
          <LsPicker kind={kind} query={q} onQueryChange={setQ} onPick={(id, t, p) => { onPick(id, t, p); setEditing(false); }} />
          {onCreate && <Button variant="ghost" size="sm" onClick={() => setCreating((v) => !v)}>+ create in LS</Button>}
          {!suggestion && kind === "brand" && !editing && <span className="text-[11px] text-slate-400">will be auto-created</span>}
          {editing && <button onClick={() => setEditing(false)} className="text-xs text-slate-500 hover:underline">cancel</button>}
        </div>
      )}
      {onCreate && creating && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/50 px-2.5 py-2 text-xs">
          <span className="text-slate-600">Create <span className="font-semibold text-slate-800">“{newName || "(type a name)"}”</span></span>
          <span className="text-slate-500">under:</span>
          <LsPicker kind="category" onPick={(id, t) => setParent({ lsId: id, lsTitle: t })} placeholder="parent (optional)" />
          {parent ? (
            <span className="inline-flex items-center gap-1"><Badge color="blue">{parent.lsTitle}</Badge><button onClick={() => setParent(null)} className="text-slate-400 hover:text-red-600" title="Top-level (no parent)">×</button></span>
          ) : (
            <span className="text-[11px] text-slate-400">top-level</span>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={!newName || busy}
            onClick={() => {
              if (!newName || busy) return;
              // Keep the form open while it runs — on success the row refreshes to the mapped view (form
              // disappears); on error the form stays so you can retry. `busy` drives the in-progress state.
              if (confirm(`Create a NEW Lightspeed category “${newName}”${parent ? ` under “${parent.lsTitle}”` : " (top-level)"} in your LIVE shop and map to it?`)) {
                onCreate(newName, parent?.lsId ?? null);
              }
            }}
          >
            {busy ? "Creating…" : "Create & map"}
          </Button>
          {busy ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-indigo-600"><span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500" />creating in Lightspeed &amp; mapping…</span>
          ) : (
            <button onClick={() => { setCreating(false); setParent(null); }} className="text-slate-500 hover:underline">cancel</button>
          )}
        </div>
      )}
    </div>
  );
}

/** Per-row extra-category chips + inline add picker for the category mapping table. */
function ExtraCatCell({
  pbsId,
  extras,
  onAdd,
  onRemove,
}: {
  pbsId: string;
  extras: { lsId: number; lsTitle: string }[];
  onAdd: (lsId: number, lsTitle: string) => void;
  onRemove: (lsId: number) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {extras.map((extra) => (
        <span key={extra.lsId} className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">
          {extra.lsTitle}
          <button onClick={() => onRemove(extra.lsId)} className="ml-0.5 leading-none text-blue-500 hover:text-blue-900" title="Remove">×</button>
        </span>
      ))}
      {adding ? (
        <div className="flex items-center gap-1">
          <LsPicker kind="category" onPick={(id, t) => { onAdd(id, t); setAdding(false); }} placeholder="search category…" />
          <button onClick={() => setAdding(false)} className="text-xs text-slate-400 hover:text-slate-600">✕</button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-400 hover:border-indigo-400 hover:text-indigo-600"
        >
          + add
        </button>
      )}
    </div>
  );
}

/* ── friendly schedule (cron) editor helpers ──────────────────────────────────────────────────────
   We only model the common shape "<min> <hour> * * <dow>" (a daily time on some weekdays). Anything
   more exotic falls back to the raw cron box. dow uses cron's 0=Sun … 6=Sat. */
type SchedDays = "weekdays" | "daily" | "custom";
interface FriendlyCron { ok: boolean; time: string; days: SchedDays; dows: number[] }
function parseFriendlyCron(cron: string): FriendlyCron {
  const p = (cron ?? "").trim().split(/\s+/);
  const bad: FriendlyCron = { ok: false, time: "08:00", days: "weekdays", dows: [1, 2, 3, 4, 5] };
  if (p.length !== 5) return bad;
  const [min, hour, dom, mon, dow] = p;
  if (!min || !hour || !dom || !mon || !dow) return bad;
  if (!/^\d{1,2}$/.test(min) || !/^\d{1,2}$/.test(hour) || dom !== "*" || mon !== "*") return bad;
  const time = `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
  if (dow === "*") return { ok: true, time, days: "daily", dows: [0, 1, 2, 3, 4, 5, 6] };
  if (dow === "1-5") return { ok: true, time, days: "weekdays", dows: [1, 2, 3, 4, 5] };
  const out = new Set<number>();
  for (const part of dow.split(",")) {
    const m = /^(\d)(?:-(\d))?$/.exec(part.trim());
    if (!m) return bad;
    const a = Number(m[1]);
    const b = m[2] != null ? Number(m[2]) : a;
    for (let v = a; v <= b; v++) out.add(v % 7);
  }
  if (!out.size) return bad;
  return { ok: true, time, days: "custom", dows: [...out].sort() };
}
function buildCron(time: string, days: SchedDays, dows: number[]): string {
  const [hh, mm] = (time || "08:00").split(":");
  const h = String(Number(hh) || 0);
  const m = String(Number(mm) || 0);
  const dowExpr = days === "weekdays" ? "1-5" : days === "daily" ? "*" : dows.length ? [...dows].sort((a, b) => a - b).join(",") : "*";
  return `${m} ${h} * * ${dowExpr}`;
}
const DOW_PICKER: { label: string; dow: number }[] = [
  { label: "Mon", dow: 1 }, { label: "Tue", dow: 2 }, { label: "Wed", dow: 3 }, { label: "Thu", dow: 4 }, { label: "Fri", dow: 5 }, { label: "Sat", dow: 6 }, { label: "Sun", dow: 0 },
];

export default function Settings() {
  const utils = trpc.useUtils();
  const cfg = trpc.config.get.useQuery();
  const [flash, setFlash] = useState<{ msg: string; ok: boolean } | null>(null);
  const showFlash = (msg: string, ok = true) => { setFlash({ msg, ok }); setTimeout(() => setFlash(null), 2800); };
  // Surface server errors instead of failing silently (e.g. a rejected insert).
  const onErr = (e: { message: string }) => showFlash(`Save failed: ${e.message}`, false);
  const ok = (msg: string) => () => { utils.config.get.invalidate(); showFlash(msg); };
  const setScalar = trpc.config.setScalar.useMutation({ onSuccess: ok("Setting saved ✓"), onError: onErr });
  const vatUpsert = trpc.config.vat.upsert.useMutation({ onSuccess: ok("VAT mapping saved ✓"), onError: onErr });
  const vatDelete = trpc.config.vat.delete.useMutation({ onSuccess: ok("VAT mapping deleted"), onError: onErr });
  const stukUpsert = trpc.config.stuk.upsert.useMutation({ onSuccess: ok("Unit saved ✓"), onError: onErr });
  const stukDelete = trpc.config.stuk.delete.useMutation({ onSuccess: ok("Unit deleted"), onError: onErr });
  const exclDelete = trpc.config.excluded.delete.useMutation({ onSuccess: ok("Exclusion removed"), onError: onErr });
  const exclUpsert = trpc.config.excluded.upsert.useMutation({ onSuccess: ok("Category excluded ✓"), onError: onErr });
  const exclSkuDelete = trpc.config.excludedSku.delete.useMutation({ onSuccess: ok("SKU exclusion removed"), onError: onErr });
  const exclSkuUpsert = trpc.config.excludedSku.upsert.useMutation({ onSuccess: ok("SKU excluded ✓"), onError: onErr });
  const keywordAdd = trpc.config.excludedKeyword.add.useMutation({ onSuccess: ok("Keyword excluded ✓"), onError: onErr });
  const keywordRemove = trpc.config.excludedKeyword.remove.useMutation({ onSuccess: ok("Keyword removed"), onError: onErr });
  const seedLossRisk = trpc.config.excludedSku.seedDropshipLossRisk.useMutation({
    onSuccess: (r) => {
      utils.config.get.invalidate();
      showFlash(
        r.added > 0
          ? `Found ${r.found} drop-ship loss-risk SKU${r.found === 1 ? "" : "s"} — added ${r.added} to Lightspeed exclusions ✓`
          : r.found > 0
            ? `${r.found} loss-risk SKUs found — all already excluded`
            : "No loss-risk SKUs found. If you haven't refreshed the ExactOnline snapshot since this update, run one first (Data Sync).",
        r.found > 0,
      );
    },
    onError: onErr,
  });
  const seedInkToner = trpc.config.seedInkTonerExclusions.useMutation({
    onSuccess: (r) => {
      utils.config.get.invalidate();
      showFlash(
        r.added > 0
          ? `Excluded ${r.added} ink/toner/cartridge categor${r.added === 1 ? "y" : "ies"} from Lightspeed ✓`
          : r.found > 0
            ? `${r.found} ink/toner categories found — all already excluded`
            : "No ink/toner/cartridge categories found in the cached list — run a Lightspeed snapshot first (Data Sync).",
        r.found > 0,
      );
    },
    onError: onErr,
  });
  const brandUpsert = trpc.config.brandPrefix.upsert.useMutation({ onSuccess: () => { utils.config.get.invalidate(); showFlash("Brand rule saved ✓"); setNewBrand({ brand: "", prefix: "", transform: "reverse", isEconomic: false }); }, onError: onErr });
  const brandDelete = trpc.config.brandPrefix.delete.useMutation({ onSuccess: ok("Brand rule deleted"), onError: onErr });
  const brandSeed = trpc.config.brandPrefix.seedFromData.useMutation({ onSuccess: ok("Brand rules re-seeded ✓"), onError: onErr });
  const aBrandManual = trpc.config.brandPrefix.setABrandManual.useMutation({
    onSuccess: (_r, v) => { utils.config.get.invalidate(); showFlash(v.verdict === null ? "Handmatig oordeel opgeheven — de schatting geldt weer" : `Handmatig vastgezet op ${v.verdict ? "A-merk" : "huismerk"} ✓`); },
    onError: onErr,
  });
  const brandClassify = trpc.config.brandPrefix.classify.useMutation({
    onSuccess: (r) => { utils.config.get.invalidate(); showFlash(`A-merk-schatting bijgewerkt: ${r.aBrand} A-merk, ${r.houseLabel} huismerk, ${r.unclear} onduidelijk, ${r.noData} zonder data ✓`); },
    onError: onErr,
  });

  // Remember the active tab across refreshes (and navigation away/back) so you land where you left off.
  const [tab, setTabState] = useState<Tab>(() => {
    try {
      const saved = localStorage.getItem("settings.tab");
      if (saved && TABS.some((t) => t.id === saved)) return saved as Tab;
    } catch { /* localStorage unavailable — fall back to default */ }
    return "general";
  });
  const setTab = (t: Tab) => { setTabState(t); try { localStorage.setItem("settings.tab", t); } catch { /* ignore */ } };

  // ── Eco rules tab ──
  const [ecoPage, setEcoPage] = useState(1);
  const [ecoBrandFilter, setEcoBrandFilter] = useState("");
  const [ecoBrandFilterDebounced, setEcoBrandFilterDebounced] = useState("");
  const [ecoUnmappedOnly, setEcoUnmappedOnly] = useState(false);
  const ecoLabels = trpc.config.ecoLabels.get.useQuery(undefined, { enabled: tab === "eco-rules" });
  const ecoStaleMappings = trpc.config.ecoPathMapping.staleMappings.useQuery(undefined, { enabled: tab === "eco-rules" });
  const [ecoTodayOpen, setEcoTodayOpen] = useState(false);
  const [ecoTodaySinceHours, setEcoTodaySinceHours] = useState(24);
  const ecoTodayCreations = trpc.config.ecoPathMapping.todayCreations.useQuery({ sinceHours: ecoTodaySinceHours }, { enabled: tab === "eco-rules" && ecoTodayOpen });
  const [ecoCleanupOpen, setEcoCleanupOpen] = useState(false);
  const [ecoCleanupThreshold, setEcoCleanupThreshold] = useState(10);
  const [ecoCleanupEnabled, setEcoCleanupEnabled] = useState(false);
  const [ecoCleanupForceKeep, setEcoCleanupForceKeep] = useState<Set<string>>(new Set());
  const [ecoCleanupForceRemap, setEcoCleanupForceRemap] = useState<Set<string>>(new Set());
  const ecoThresholdAnalysis = trpc.config.ecoPathMapping.thresholdAnalysis.useQuery(
    { sinceHours: 24, threshold: ecoCleanupThreshold },
    { enabled: tab === "eco-rules" && ecoCleanupEnabled },
  );
  const ecoResults = trpc.config.ecoRuleResults.useQuery({ page: ecoPage, pageSize: 100, brandFilter: ecoBrandFilterDebounced || undefined, unmappedBrandOnly: ecoUnmappedOnly || undefined }, { enabled: tab === "eco-rules" });
  const ecoLabelSet = trpc.config.ecoLabels.set.useMutation({ onSuccess: () => { ecoLabels.refetch(); showFlash("Eco label saved ✓"); }, onError: onErr });
  const ecoLabelDelete = trpc.config.ecoLabels.delete.useMutation({ onSuccess: () => { ecoLabels.refetch(); showFlash("Eco label removed"); }, onError: onErr });
  const [ecoLabelPicker, setEcoLabelPicker] = useState<"ecologic" | "illogic" | "economic" | "economicEcologic" | null>(null);
  // Eco auto-map (preview + selective apply)
  type EcoAutoSuggestion = { pbsId: string; pbsName: string; pbsPath: string; ecologic: { lsId: number; lsTitle: string; lsPath: string } | null; illogic: { lsId: number; lsTitle: string; lsPath: string } | null; economic: { lsId: number; lsTitle: string; lsPath: string } | null; economicEcologic: { lsId: number; lsTitle: string; lsPath: string } | null; matchCount: number };
  const [ecoAutoPreview, setEcoAutoPreview] = useState<{ suggestions: EcoAutoSuggestion[]; totalPbs: number; totalMatched: number; unconfiguredRoots: string[] } | null>(null);
  const [selEcoRows, setSelEcoRows] = useState<Set<string>>(new Set());
  const [ecoAutoSort, setEcoAutoSort] = useState<"coverage-asc" | "coverage-desc" | "pbs">("coverage-asc");
  const [ecoAutoCompact, setEcoAutoCompact] = useState(false);
  const [ecoDepthFilter, setEcoDepthFilter] = useState<number | null>(null);
  const ecoRangeAnchor = useRef<string | null>(null);
  // Per-cell editor state for the auto-map preview table
  const [ecoEditCell, setEcoEditCell] = useState<string | null>(null); // "pbsId:ecoKey"
  const [ecoEditCreating, setEcoEditCreating] = useState(false);
  const [ecoEditParent, setEcoEditParent] = useState<{ lsId: number; lsTitle: string } | null>(null);
  const [ecoEditQuery, setEcoEditQuery] = useState("");
  // Additional eco trees to create in simultaneously (key = ecoKey, value = chosen parent or null)
  const [ecoCreateAdditional, setEcoCreateAdditional] = useState<Partial<Record<"ecologic" | "illogic" | "economic" | "economicEcologic", { lsId: number; lsTitle: string } | null>>>({});
  const [ecoCreateSubSegments, setEcoCreateSubSegments] = useState<string[]>([]); // extra levels below the first
  const closeEcoEditor = () => { setEcoEditCell(null); setEcoEditCreating(false); setEcoEditParent(null); setEcoEditQuery(""); setEcoCreateAdditional({}); setEcoCreateSubSegments([]); };
  // Bulk set: override one or more eco tree columns for all selected rows
  const [ecoBulkOpen, setEcoBulkOpen] = useState(false);
  const [ecoBulkKeys, setEcoBulkKeys] = useState<Set<"ecologic" | "illogic" | "economic" | "economicEcologic">>(new Set());
  const [ecoBulkPicks, setEcoBulkPicks] = useState<Partial<Record<"ecologic" | "illogic" | "economic" | "economicEcologic", { lsId: number; lsTitle: string; lsPath?: string }>>>({});
  const [ecoBulkQueries, setEcoBulkQueries] = useState<Partial<Record<"ecologic" | "illogic" | "economic" | "economicEcologic", string>>>({});
  const updateEcoPreviewCell = (pbsCategoryId: string, ecoKey: string, lsId: number, lsTitle: string, lsPath?: string) => {
    setEcoAutoPreview((prev) => {
      if (!prev) return prev;
      return { ...prev, suggestions: prev.suggestions.map((s) => {
        if (s.pbsId !== pbsCategoryId) return s;
        const updated = { ...s, [ecoKey]: { lsId, lsTitle, lsPath: lsPath ?? lsTitle } };
        const matchCount = (["ecologic", "illogic", "economic", "economicEcologic"] as const).filter((k) => updated[k] !== null).length;
        return { ...updated, matchCount };
      }) };
    });
  };
  const ecoDeleteEntry = trpc.config.ecoPathMapping.delete.useMutation({
    onSuccess: () => { ecoStaleMappings.refetch(); showFlash("Stale mapping removed ✓"); },
    onError: onErr,
  });
  const [ecoOrphanPreview, setEcoOrphanPreview] = useState<{ lsId: number; lsTitle: string; lsPath: string }[] | null>(null);
  const ecoClearAllMappings = trpc.config.ecoPathMapping.clearAllMappings.useMutation({
    onSuccess: (data) => { showFlash(`Cleared ${data.removed} eco path mapping${data.removed === 1 ? "" : "s"} — clean slate ✓`); ecoStaleMappings.refetch(); },
    onError: onErr,
  });
  const ecoDeleteOrphans = trpc.config.ecoPathMapping.deleteOrphanedLsCategories.useMutation({
    onSuccess: (data) => {
      if (data.dryRun) { setEcoOrphanPreview(data.orphans); return; }
      const msg = `Deleted ${data.deleted.length} orphaned LS categor${data.deleted.length === 1 ? "y" : "ies"}` + (data.failed.length ? ` (${data.failed.length} failed)` : "") + " ✓";
      showFlash(msg);
      setEcoOrphanPreview(null);
      ecoTodayCreations.refetch();
      ecoStaleMappings.refetch();
    },
    onError: onErr,
  });
  const [ecoRevertOpen, setEcoRevertOpen] = useState(false);
  const [ecoRevertThreshold, setEcoRevertThreshold] = useState(10);
  const [ecoRevertSinceHours, setEcoRevertSinceHours] = useState(72);
  const ecoRevertAnalysis = trpc.config.ecoPathMapping.findRevertable.useQuery(
    { sinceHours: ecoRevertSinceHours, threshold: ecoRevertThreshold },
    { enabled: tab === "eco-rules" && ecoRevertOpen },
  );
  const ecoRevertMutation = trpc.config.ecoPathMapping.revertToOriginal.useMutation({
    onSuccess: (data) => {
      showFlash(`Reverted ${data.reverted} mapping${data.reverted === 1 ? "" : "s"} to original LS category ✓`);
      ecoRevertAnalysis.refetch();
      ecoStaleMappings.refetch();
    },
    onError: onErr,
  });
  const ecoCleanupMutation = trpc.config.ecoPathMapping.cleanupBelowThreshold.useMutation({
    onSuccess: (data) => {
      const msg = `Remapped ${data.remapped} mapping${data.remapped === 1 ? "" : "s"}, deleted ${data.deleted.length} LS categor${data.deleted.length === 1 ? "y" : "ies"}` + (data.failed.length ? ` (${data.failed.length} failed)` : "") + " ✓";
      showFlash(msg);
      ecoThresholdAnalysis.refetch();
      ecoStaleMappings.refetch();
    },
    onError: onErr,
  });
  const ecoUpsertEntry = trpc.config.ecoPathMapping.upsert.useMutation({
    onSuccess: (_, v) => { updateEcoPreviewCell(v.pbsCategoryId, v.key, v.lsId, v.lsTitle); closeEcoEditor(); showFlash("Eco path saved ✓"); },
    onError: onErr,
  });
  const ecoCreateLsCategory = trpc.config.ecoPathMapping.createLsCategory.useMutation({
    onSuccess: (data, v) => {
      updateEcoPreviewCell(v.pbsCategoryId, v.ecoKey, data.lsId, v.title, data.lsPath);
      closeEcoEditor();
      showFlash("LS category created — refreshing suggestions…");
      ecoAutoSuggest.mutate();
    },
    onError: onErr,
  });
  const ecoCreateLsCategoryForTrees = trpc.config.ecoPathMapping.createLsCategoryForTrees.useMutation({
    onSuccess: (data, v) => {
      data.results.forEach((r) => updateEcoPreviewCell(v.pbsCategoryId, r.ecoKey, r.lsId, v.title, r.lsPath));
      closeEcoEditor();
      showFlash(`LS category created in ${data.results.length} tree${data.results.length === 1 ? "" : "s"} — refreshing suggestions…`);
      ecoAutoSuggest.mutate();
    },
    onError: onErr,
  });
  const ecoCreateLsCategoryPath = trpc.config.ecoPathMapping.createLsCategoryPath.useMutation({
    onSuccess: (data, v) => {
      updateEcoPreviewCell(v.pbsCategoryId, v.ecoKey, data.lsId, v.segments[v.segments.length - 1] ?? "", data.lsPath);
      closeEcoEditor();
      showFlash("LS category path created — refreshing suggestions…");
      ecoAutoSuggest.mutate();
    },
    onError: onErr,
  });
  const ecoCreateLsCategoryPathForTrees = trpc.config.ecoPathMapping.createLsCategoryPathForTrees.useMutation({
    onSuccess: (data, v) => {
      data.results.forEach((r) => updateEcoPreviewCell(v.pbsCategoryId, r.ecoKey, r.lsId, v.segments[v.segments.length - 1] ?? "", r.lsPath));
      closeEcoEditor();
      showFlash(`LS category path created in ${data.results.length} tree${data.results.length === 1 ? "" : "s"} — refreshing suggestions…`);
      ecoAutoSuggest.mutate();
    },
    onError: onErr,
  });
  const ecoAutoSuggest = trpc.config.ecoPathMapping.autoSuggest.useMutation({ onSuccess: (r) => { setEcoAutoPreview(r); setSelEcoRows(new Set()); }, onError: onErr });
  const ecoAutoApply = trpc.config.ecoPathMapping.autoApply.useMutation({
    onSuccess: (r, vars) => {
      const appliedIds = vars?.pbsCategoryIds ? new Set(vars.pbsCategoryIds) : null;
      setEcoAutoPreview((prev) => {
        if (!prev) return prev;
        const remaining = appliedIds ? prev.suggestions.filter((s) => !appliedIds.has(s.pbsId)) : [];
        return remaining.length === 0 ? null : { ...prev, suggestions: remaining, totalMatched: remaining.length };
      });
      setSelEcoRows(new Set());
      showFlash(`Applied ${r.added} eco path entr${r.added === 1 ? "y" : "ies"} ✓`);
    },
    onError: onErr,
  });
  const ecoBulkUpsert = trpc.config.ecoPathMapping.bulkUpsert.useMutation({
    onSuccess: (r, vars) => {
      const ids = new Set(vars.pbsCategoryIds);
      setEcoAutoPreview((prev) => {
        if (!prev) return prev;
        const updated = prev.suggestions.map((s) => {
          if (!ids.has(s.pbsId)) return s;
          const updated = { ...s, [vars.key]: { lsId: vars.lsId, lsTitle: vars.lsTitle, lsPath: vars.lsTitle } };
          const matchCount = (["ecologic", "illogic", "economic", "economicEcologic"] as const).filter((k) => updated[k] !== null).length;
          return { ...updated, matchCount };
        });
        return { ...prev, suggestions: updated };
      });
      setEcoBulkOpen(false);
      setEcoBulkKeys(new Set());
      setEcoBulkPicks({});
      setEcoBulkQueries({});
      showFlash(`Set ${r.updated} eco path entr${r.updated === 1 ? "y" : "ies"} ✓`);
    },
    onError: onErr,
  });
  const ecoBulkUpsertMulti = trpc.config.ecoPathMapping.bulkUpsertMulti.useMutation({
    onSuccess: (r, vars) => {
      const ids = new Set(vars.pbsCategoryIds);
      setEcoAutoPreview((prev) => {
        if (!prev) return prev;
        const updated = prev.suggestions.map((s) => {
          if (!ids.has(s.pbsId)) return s;
          let row = { ...s };
          for (const tree of vars.trees) {
            row = { ...row, [tree.key]: { lsId: tree.lsId, lsTitle: tree.lsTitle, lsPath: tree.lsPath ?? tree.lsTitle } };
          }
          const matchCount = (["ecologic", "illogic", "economic", "economicEcologic"] as const).filter((k) => row[k] !== null).length;
          return { ...row, matchCount };
        });
        return { ...prev, suggestions: updated };
      });
      setEcoBulkOpen(false);
      setEcoBulkKeys(new Set());
      setEcoBulkPicks({});
      setEcoBulkQueries({});
      showFlash(`Set ${r.updated} eco path entr${r.updated === 1 ? "y" : "ies"} ✓`);
    },
    onError: onErr,
  });

  // ── Users tab (admin only) ──
  const me = trpc.auth.me.useQuery();
  const isAdmin = me.data?.role === "admin";
  const usersList = trpc.auth.users.list.useQuery(undefined, { enabled: isAdmin && tab === "users" });
  const [newUser, setNewUser] = useState<{ email: string; name: string; role: "user" | "admin" }>({ email: "", name: "", role: "user" });
  const [tempCred, setTempCred] = useState<{ email: string; tempPassword: string } | null>(null);
  const okUsers = (msg: string) => () => { utils.auth.users.list.invalidate(); showFlash(msg); };
  const userAdd = trpc.auth.users.add.useMutation({ onSuccess: (r) => { utils.auth.users.list.invalidate(); setTempCred(r); setNewUser({ email: "", name: "", role: "user" }); showFlash(`User ${r.email} added ✓`); }, onError: onErr });
  const userSetRole = trpc.auth.users.setRole.useMutation({ onSuccess: okUsers("Role updated ✓"), onError: onErr });
  const userReset = trpc.auth.users.resetPassword.useMutation({ onSuccess: (r) => { setTempCred(r); showFlash(`New temporary password for ${r.email} ✓`); }, onError: onErr });
  const userRemove = trpc.auth.users.remove.useMutation({ onSuccess: okUsers("User removed"), onError: onErr });

  // ── ExactOnline data source tab (admin) ──
  const exactSrc = trpc.config.exactSource.get.useQuery(undefined, { enabled: isAdmin && tab === "exactsrc" });
  const [repl, setRepl] = useState({ server: "", port: "1433", database: "", user: "", password: "", encrypt: true, trustServerCertificate: true });
  const [replLoaded, setReplLoaded] = useState(false);
  const [replTestResult, setReplTestResult] = useState<{ ok: boolean; message: string; counts?: { items: number; supplierItems: number; activeSalesPrices: number } } | null>(null);
  useEffect(() => {
    if (exactSrc.data && !replLoaded) {
      const r = exactSrc.data.replica;
      setRepl({ server: r.server, port: String(r.port), database: r.database, user: r.user, password: r.password ?? "", encrypt: r.encrypt, trustServerCertificate: r.trustServerCertificate });
      setReplLoaded(true);
    }
  }, [exactSrc.data, replLoaded]);
  const okExact = (msg: string) => () => { utils.config.exactSource.get.invalidate(); showFlash(msg); };
  const replSave = trpc.config.exactSource.save.useMutation({ onSuccess: okExact("Replica settings saved ✓"), onError: onErr });
  const setExactSrc = trpc.config.exactSource.setSource.useMutation({ onSuccess: (r) => okExact(`Primary source set to ${r.source === "replica" ? "Replica (MSSQL)" : "ExactOnline via MCP"} ✓`)(), onError: onErr });
  const setVerifyWrites = trpc.config.exactSource.setVerifyWrites.useMutation({ onSuccess: (r) => okExact(`Write verification ${r.on ? "on" : "off"} ✓`)(), onError: onErr });
  const replTest = trpc.config.exactSource.test.useMutation({ onSuccess: (r) => setReplTestResult(r), onError: (e) => setReplTestResult({ ok: false, message: e.message }) });
  // ── ExactOnline / MCP connection (admin) ──
  const [mcp, setMcp] = useState({ url: "", key: "", division: "", itemUrlBase: "", supplierCode: "" });
  const [showSecrets, setShowSecrets] = useState(true); // reveal the API key + DB password in the inputs
  const [mcpLoaded, setMcpLoaded] = useState(false);
  const [mcpTestResult, setMcpTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  useEffect(() => {
    if (exactSrc.data && !mcpLoaded) {
      const m = exactSrc.data.mcp;
      setMcp({ url: m.url, key: m.key ?? "", division: String(m.division), itemUrlBase: m.itemUrlBase, supplierCode: exactSrc.data.supplierCode });
      setMcpLoaded(true);
    }
  }, [exactSrc.data, mcpLoaded]);
  const mcpSave = trpc.config.exactSource.saveMcp.useMutation({ onSuccess: okExact("ExactOnline / MCP settings saved ✓"), onError: onErr });
  const mcpTest = trpc.config.exactSource.testMcp.useMutation({ onSuccess: (r) => setMcpTestResult(r), onError: (e) => setMcpTestResult({ ok: false, message: e.message }) });
  const [divisions, setDivisions] = useState<{ code: number; description: string; current: boolean }[] | null>(null);
  const mcpDivisions = trpc.config.exactSource.divisions.useMutation({ onSuccess: (r) => { setDivisions(r.divisions); if (!r.ok) showFlash(`Could not load divisions: ${r.message}`); else if (!r.divisions.length) showFlash(r.message || "MCP returned no divisions for this key. You can still type the division number manually."); else showFlash(`Loaded ${r.divisions.length} division(s).`); }, onError: onErr });

  // ── PBS data source tab (admin) — the FTP feed the whole catalog is built from ──
  const pbsSrc = trpc.config.pbsSource.get.useQuery(undefined, { enabled: isAdmin && tab === "pbssrc" });
  const [pbs, setPbs] = useState({ host: "", user: "", password: "", dir: "", secure: false });
  const [pbsLoaded, setPbsLoaded] = useState(false);
  const [showPbsSecret, setShowPbsSecret] = useState(false);
  const [pbsTestResult, setPbsTestResult] = useState<{ ok: boolean; message: string; files: { name: string; size: number | null; modifiedAt: string | null }[] } | null>(null);
  useEffect(() => {
    if (pbsSrc.data && !pbsLoaded) {
      const s = pbsSrc.data;
      setPbs({ host: s.host, user: s.user, password: s.password ?? "", dir: s.dir, secure: s.secure });
      setPbsLoaded(true);
    }
  }, [pbsSrc.data, pbsLoaded]);
  // ── Lightspeed tab (admin) — API credentials, editable here because nobody can reach the server .env ──
  const lsSrc = trpc.config.lsSource.get.useQuery(undefined, { enabled: isAdmin && tab === "lssrc" });
  const [ls, setLs] = useState({ key: "", secret: "", key2: "", secret2: "", key3: "", secret3: "" });
  const [lsLoaded, setLsLoaded] = useState(false);
  const [showLsSecret, setShowLsSecret] = useState(false);
  const [lsTestResult, setLsTestResult] = useState<{ ok: boolean; results: { label: string; ok: boolean; message: string }[] } | null>(null);
  useEffect(() => {
    if (lsSrc.data && !lsLoaded) {
      setLs({ key: lsSrc.data.key ?? "", secret: lsSrc.data.secret ?? "", key2: lsSrc.data.key2 ?? "", secret2: lsSrc.data.secret2 ?? "", key3: lsSrc.data.key3 ?? "", secret3: lsSrc.data.secret3 ?? "" });
      setLsLoaded(true);
    }
  }, [lsSrc.data, lsLoaded]);
  const lsSave = trpc.config.lsSource.save.useMutation({ onSuccess: () => { utils.config.lsSource.get.invalidate(); showFlash("Lightspeed credentials saved ✓"); }, onError: onErr });
  const lsTest = trpc.config.lsSource.test.useMutation({ onSuccess: (r) => setLsTestResult(r), onError: onErr });
  // Live headroom per key. Not polled: reading it costs an API call of the very budget it reports.
  const lsLimits = trpc.config.lsSource.limits.useQuery(undefined, { enabled: isAdmin && tab === "lssrc", refetchOnWindowFocus: false, staleTime: 60_000 });

  const pbsSave = trpc.config.pbsSource.save.useMutation({ onSuccess: () => { utils.config.pbsSource.get.invalidate(); showFlash("PBS data source saved ✓"); }, onError: onErr });
  const pbsTest = trpc.config.pbsSource.test.useMutation({ onSuccess: (r) => setPbsTestResult(r), onError: (e) => setPbsTestResult({ ok: false, message: e.message, files: [] }) });

  // ── Schedules tab (admin) ──
  const schedList = trpc.config.schedules.list.useQuery(undefined, { enabled: isAdmin && tab === "schedules" });
  const [schedRows, setSchedRows] = useState<{ id: string; label: string; cron: string; enabled: boolean; lastRun: string | null; nextRun: string | null }[] | null>(null);
  useEffect(() => { if (schedList.data && !schedRows) setSchedRows(schedList.data); }, [schedList.data, schedRows]);
  const schedSave = trpc.config.schedules.save.useMutation({ onSuccess: (rows) => { setSchedRows(rows); utils.config.schedules.list.invalidate(); showFlash("Schedules saved ✓"); }, onError: onErr });
  const [cronAdvanced, setCronAdvanced] = useState(false);
  const setSchedCron = (i: number, cron: string) => setSchedRows((rows) => (rows ? rows.map((r, j) => (j === i ? { ...r, cron } : r)) : rows));

  // ── Mapping tab (category + brand) ──
  const [mapQ, setMapQ] = useState("");
  // Default to showing only UNMAPPED rows — the common task is working through what still needs mapping.
  // Untick to show all rows (mapped + unmapped), e.g. to review or change an existing mapping.
  const [mapUnmapped, setMapUnmapped] = useState(true);
  const [mapSub, setMapSubState] = useState<"categories" | "brands">("categories");
  // Sorting + filtering for the mapping tables.
  const [catSort, setCatSort] = useState<"products" | "breadcrumb" | "score-desc" | "score-asc" | "mapping-asc" | "mapping-desc">("products");
  const [brandSort, setBrandSort] = useState<"products" | "name" | "score-desc" | "score-asc">("products");
  const [mapBranch, setMapBranch] = useState(""); // category breadcrumb-branch filter ("" = all)
  const [mapMatch, setMapMatch] = useState<"all" | "strong" | "weak" | "none">("all"); // suggestion-score band
  const [mapCompact, setMapCompact] = useState(false); // compact row mode for denser review
  // Bulk "assign to selected": a set of checked rows per sub-tab + one chosen LS target.
  const [selCats, setSelCats] = useState<Set<string>>(new Set());
  const [selBrands, setSelBrands] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<{ lsId: number; lsTitle: string } | null>(null);
  // Switching sub-tab resets the (kind-specific) bulk target so a category target can't leak into brands.
  const setMapSub = (s: "categories" | "brands") => { setMapSubState(s); setBulkTarget(null); };
  // Only load the sub-tab you're looking at — running both the category AND brand list (each scans the
  // whole catalog) on every Mapping open was the slow part. The other loads when you switch to it.
  const catMapList = trpc.config.categoryMapping.list.useQuery({ q: mapQ, onlyUnmapped: mapUnmapped, sort: catSort, branch: mapBranch, match: mapMatch }, { enabled: tab === "mapping" && mapSub === "categories" });
  const brandMapList = trpc.config.brandMapping.list.useQuery({ q: mapQ, onlyUnmapped: mapUnmapped, sort: brandSort, match: mapMatch }, { enabled: tab === "mapping" && mapSub === "brands" });
  const okMap = (msg: string) => () => { utils.config.categoryMapping.list.invalidate(); utils.config.brandMapping.list.invalidate(); showFlash(msg); };
  const catMapUpsert = trpc.config.categoryMapping.upsert.useMutation({ onSuccess: okMap("Category mapped ✓"), onError: onErr });
  const catMapDelete = trpc.config.categoryMapping.delete.useMutation({ onSuccess: okMap("Mapping removed"), onError: onErr });
  const catCreate = trpc.config.categoryMapping.createLsCategory.useMutation({ onSuccess: okMap("LS category created ✓"), onError: onErr });
  const catExtraMap = trpc.config.categoryMapping.extraList.useQuery(undefined, { enabled: tab === "mapping" && mapSub === "categories" });
  const catExtraUpsert = trpc.config.categoryMapping.extraUpsert.useMutation({ onSuccess: () => { utils.config.categoryMapping.extraList.invalidate(); showFlash("Extra category added ✓"); }, onError: onErr });
  const catExtraDelete = trpc.config.categoryMapping.extraDelete.useMutation({ onSuccess: () => { utils.config.categoryMapping.extraList.invalidate(); showFlash("Extra category removed"); }, onError: onErr });
  const catExtraBulkAdd = trpc.config.categoryMapping.extraBulkAdd.useMutation({ onSuccess: (r) => { utils.config.categoryMapping.extraList.invalidate(); setSelCats(new Set()); setBulkExtraTarget(null); showFlash(`Extra category added to ${r.added} categories ✓`); }, onError: onErr });
  const [bulkExtraTarget, setBulkExtraTarget] = useState<{ lsId: number; lsTitle: string } | null>(null);
  const [extraCatsPreviewResult, setExtraCatsPreviewResult] = useState<{ products: number; linkActions: number } | null>(null);
  const extraCatsPreviewMut = trpc.config.categoryMapping.extraCatsPreview.useMutation({
    onSuccess: (r) => { setExtraCatsPreviewResult(r); },
    onError: onErr,
  });
  const extraCatsApplyMut = trpc.config.categoryMapping.extraCatsApply.useMutation({
    onSuccess: () => { setExtraCatsPreviewResult(null); showFlash("Started ✓ — extra categories are being linked in the background. Check the server logs for the final result."); },
    onError: onErr,
  });
  const brandMapUpsert = trpc.config.brandMapping.upsert.useMutation({ onSuccess: okMap("Brand mapped ✓"), onError: onErr });
  const brandMapDelete = trpc.config.brandMapping.delete.useMutation({ onSuccess: okMap("Mapping removed"), onError: onErr });
  const catBulk = trpc.config.categoryMapping.bulkAssign.useMutation({
    onSuccess: (r) => { utils.config.categoryMapping.list.invalidate(); setSelCats(new Set()); setBulkTarget(null); showFlash(`Assigned ${r.assigned} categor${r.assigned === 1 ? "y" : "ies"} ✓`); },
    onError: onErr,
  });
  const brandBulk = trpc.config.brandMapping.bulkAssign.useMutation({
    onSuccess: (r) => { utils.config.brandMapping.list.invalidate(); setSelBrands(new Set()); setBulkTarget(null); showFlash(`Assigned ${r.assigned} brand${r.assigned === 1 ? "" : "s"} ✓`); },
    onError: onErr,
  });
  const catExclude = trpc.config.categoryMapping.bulkExclude.useMutation({
    onSuccess: (r) => { utils.config.categoryMapping.list.invalidate(); utils.config.get.invalidate(); setSelCats(new Set()); showFlash(`Excluded ${r.added} categor${r.added === 1 ? "y" : "ies"} ✓`); },
    onError: onErr,
  });
  // Bulk-selection helpers (operate on the currently visible rows).
  const catIds = catMapList.data?.items.map((it) => it.pbsId) ?? [];
  const brandIds = brandMapList.data?.items.map((it) => it.pbsBrand) ?? [];
  const toggleId = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setter(n); };
  // Anchor for shift-click range selection — stored as the row id (not index) so it survives re-sorts/
  // filters: the index is recomputed against the CURRENT visible list at click time. One ref is enough
  // because only one sub-tab's table is mounted at a time.
  const rangeAnchor = useRef<string | null>(null);
  // Click handler for a row checkbox. Plain click = toggle (and set the anchor). Shift+click = add every
  // row between the anchor and the clicked row to the selection ("select all in between"). Falls back to
  // a plain toggle if there's no valid anchor in the current list.
  const selectRow = (
    ids: string[],
    clickedId: string,
    shiftKey: boolean,
    set: Set<string>,
    setter: (s: Set<string>) => void,
    anchorRef?: { current: string | null },
  ) => {
    const ref = anchorRef ?? rangeAnchor;
    const anchor = ref.current;
    if (shiftKey && anchor && anchor !== clickedId) {
      const a = ids.indexOf(anchor);
      const b = ids.indexOf(clickedId);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const n = new Set(set);
        for (let i = lo; i <= hi; i++) n.add(ids[i]!);
        setter(n);
        return; // keep the anchor so the range can be re-extended
      }
    }
    toggleId(set, setter, clickedId);
    ref.current = clickedId;
  };
  const toggleAll = (visible: string[], set: Set<string>, setter: (s: Set<string>) => void) => {
    const all = visible.length > 0 && visible.every((id) => set.has(id));
    const n = new Set(set);
    visible.forEach((id) => (all ? n.delete(id) : n.add(id)));
    setter(n);
  };
  const selCount = mapSub === "categories" ? selCats.size : selBrands.size;
  const bulkBusy = catBulk.isPending || brandBulk.isPending || catExclude.isPending;
  // Exclude the selected PBS categories (system exact → hidden from Mapping + skipped in PBS → ExactOnline).
  const excludeBulk = () => {
    const ids = [...selCats];
    if (ids.length && confirm(`Exclude ${ids.length} categor${ids.length === 1 ? "y" : "ies"} from the webshop and the PBS → ExactOnline sync? You can undo this in Settings → Exclusions.`)) {
      catExclude.mutate({ pbsCategoryIds: ids, system: "exact" });
    }
  };
  // "Select all N matching" — the server returns every id matching the current filters (not just the
  // rendered page), so we can bulk-assign a whole branch without paging through it.
  const matchingCount = (mapSub === "categories" ? catMapList.data?.matchingCount : brandMapList.data?.matchingCount) ?? 0;
  const renderedCount = (mapSub === "categories" ? catMapList.data?.items.length : brandMapList.data?.items.length) ?? 0;
  const selectAllMatching = () => (mapSub === "categories" ? setSelCats(new Set(catMapList.data?.allIds ?? [])) : setSelBrands(new Set(brandMapList.data?.allIds ?? [])));
  const assignBulk = () => {
    if (!bulkTarget) return;
    if (mapSub === "categories") {
      const ids = [...selCats];
      if (ids.length && confirm(`Assign “${bulkTarget.lsTitle}” to ${ids.length} selected categor${ids.length === 1 ? "y" : "ies"}?`)) catBulk.mutate({ pbsCategoryIds: ids, lsId: bulkTarget.lsId, lsTitle: bulkTarget.lsTitle });
    } else {
      const ids = [...selBrands];
      if (ids.length && confirm(`Assign “${bulkTarget.lsTitle}” to ${ids.length} selected brand${ids.length === 1 ? "" : "s"}?`)) brandBulk.mutate({ pbsBrands: ids, lsId: bulkTarget.lsId, lsTitle: bulkTarget.lsTitle });
    }
  };
  const catAutoMap = trpc.config.categoryMapping.autoMap.useMutation({ onSuccess: (r) => okMap(`Auto-mapped ${r.mapped} categor${r.mapped === 1 ? "y" : "ies"} with an exact name match ✓`)(), onError: onErr });
  const brandAutoMap = trpc.config.brandMapping.autoMap.useMutation({ onSuccess: (r) => okMap(`Auto-mapped ${r.mapped} brand${r.mapped === 1 ? "" : "s"} with an exact name match ✓`)(), onError: onErr });
  const [stukSearch, setStukSearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [newVat, setNewVat] = useState({ vatRate: "", salesVatCode: "" });
  const [newStuk, setNewStuk] = useState({ pickSize: "", unitCode: "" });
  const [newExcl, setNewExcl] = useState<{ system: ExclSystem; categoryId: string }>({ system: "exact", categoryId: "" });
  const [treeOpen, setTreeOpen] = useState(false); // PBS category tree picker (Exclusions tab)
  const [newExclSku, setNewExclSku] = useState<{ system: ExclSystem; sku: string }>({ system: "exact", sku: "" });
  const [newKeyword, setNewKeyword] = useState("");
  const [newKeywordSystem, setNewKeywordSystem] = useState<"lightspeed" | "exact" | "both">("lightspeed");
  const [newBrand, setNewBrand] = useState({ brand: "", prefix: "", transform: "reverse" as "identity" | "reverse", isEconomic: false });
  // Brand-rule column filters (client-side: the whole table is already loaded) + inline editing.
  const [fBrand, setFBrand] = useState<string[]>([]);
  const [fPrefix, setFPrefix] = useState<string[]>([]);
  const [fTransform, setFTransform] = useState<string[]>([]);
  const [fConfidence, setFConfidence] = useState<string[]>([]);
  const [fEconomic, setFEconomic] = useState<string[]>([]);
  const [fABrand, setFABrand] = useState<string[]>([]);
  const [editBrand, setEditBrand] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ prefix: "", transform: "reverse" as "identity" | "reverse", isEconomic: false });

  if (cfg.isLoading) return <Spinner label="Loading settings…" />;
  const data = cfg.data;
  if (!data) return <p className="text-sm text-slate-500">Could not load settings.</p>;

  // App-MANAGED / sensitive scalars — never shown in the raw editor: caches & JSON blobs render as
  // "[object Object]" and are owned by jobs / the Mapping & Exclusions tabs; auth.* (session secret,
  // password hashes) and system.* (write mode) must never be displayed or hand-edited here.
  const isManagedScalar = (key: string) =>
    key.startsWith("ls.") || key.startsWith("mapping.") || key.startsWith("excluded.") || key.startsWith("sync.") || key.startsWith("auth.") || key.startsWith("system.") || key.startsWith("apply.") || key.startsWith("exact.replica") || key === "exact.readSource" || key === "exact.verifyWrites" || key === "exact.mcp";
  const scalars = (data.scalars as Scalar[])
    .filter((s) => !isManagedScalar(s.key))
    .slice()
    .sort((a, b) => a.category.localeCompare(b.category) || a.key.localeCompare(b.key));
  const stuks = data.stukUnits.filter((u) => !stukSearch || u.pickSize.includes(stukSearch) || u.unitCode.includes(stukSearch));
  /** Confidence is a number; as a filter it only makes sense in bands. */
  const confBand = (c: number | null | undefined): string =>
    c == null ? "no data" : c === 100 ? "100%" : c >= 80 ? "80–99%" : c >= 50 ? "50–79%" : "below 50%";
  /** The A-brand verdict. A manual decision beats the estimate outright; same thresholds as the
   *  server (server/config/brandClass.ts). */
  const aBrandVerdict = (b: { aBrandScore?: number | null; aBrandManual?: boolean | null }): string =>
    b.aBrandManual != null ? (b.aBrandManual ? "A-merk (handmatig)" : "huismerk (handmatig)")
    : b.aBrandScore == null ? "not scored"
    : b.aBrandScore >= 70 ? "A-merk"
    : b.aBrandScore <= 30 ? "huismerk"
    : "onduidelijk";
  /** An A-brand marked Economic contradicts itself: A-brands are normally not the cheap alternative.
   *  Shown rather than corrected — the flag drives which shelf products land on, so a human decides. */
  const aBrandConflict = (b: { isEconomic?: boolean; aBrandScore?: number | null; aBrandManual?: boolean | null }): boolean =>
    !!b.isEconomic && (b.aBrandManual === true || (b.aBrandManual == null && (b.aBrandScore ?? 0) >= 70));
  const allBrands = data.brandPrefixMap ?? [];
  const brandOptions = <T,>(pick: (b: (typeof allBrands)[number]) => T) => {
    const counts = new Map<string, number>();
    for (const b of allBrands) {
      const v = String(pick(b) ?? "");
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([value, count]) => ({ value, count }));
  };
  const brands = allBrands.filter((b) => {
    if (brandSearch && !(b.brand.toLowerCase().includes(brandSearch.toLowerCase()) || b.prefix.toLowerCase().includes(brandSearch.toLowerCase()))) return false;
    if (fBrand.length && !fBrand.includes(b.brand)) return false;
    if (fPrefix.length && !fPrefix.includes(b.prefix || "")) return false;
    if (fTransform.length && !fTransform.includes(b.transform)) return false;
    if (fConfidence.length && !fConfidence.includes(confBand(b.confidence))) return false;
    if (fEconomic.length && !fEconomic.includes(b.isEconomic ? "yes" : "no")) return false;
    if (fABrand.length && !fABrand.includes(aBrandVerdict(b))) return false;
    return true;
  });
  const brandFiltersActive = fBrand.length + fPrefix.length + fTransform.length + fConfidence.length + fEconomic.length + fABrand.length > 0;
  const clearBrandFilters = () => { setFBrand([]); setFPrefix([]); setFTransform([]); setFConfidence([]); setFEconomic([]); setFABrand([]); };

  const addExcl = (categoryId: string, label?: string) => {
    if (!categoryId.trim()) return;
    exclUpsert.mutate({ system: newExcl.system, categoryId: categoryId.trim(), label });
    setNewExcl({ ...newExcl, categoryId: "" });
  };
  const addExclSku = () => {
    const sku = newExclSku.sku.trim();
    if (!sku) return;
    exclSkuUpsert.mutate({ system: newExclSku.system, sku }); // name auto-filled server-side
    setNewExclSku({ ...newExclSku, sku: "" });
  };
  const addKeyword = () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    const systems: ("lightspeed" | "exact")[] = newKeywordSystem === "both" ? ["lightspeed", "exact"] : [newKeywordSystem];
    keywordAdd.mutate({ keyword: kw, systems });
    setNewKeyword("");
  };
  // Flatten the per-system SKU exclusions into rows for the table.
  const skuRows = (["exact", "pbs", "lightspeed"] as ExclSystem[]).flatMap((system) =>
    ((data.excludedSkus?.[system] ?? []) as { sku: string; label: string | null }[]).map((e) => ({ system, ...e })),
  );

  return (
    <div className="space-y-6">
      {/* Save/delete feedback toast */}
      {flash && (
        <div className={`fixed bottom-5 right-5 z-50 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${flash.ok ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
          {flash.msg}
        </div>
      )}
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-slate-500">All business values live here — editable without a redeploy. Changes are audited. Admin only.</p>
      </div>

      {/* Tabs — one section at a time instead of one long scroll. */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.filter((t) => (t.id !== "users" && t.id !== "exactsrc" && t.id !== "pbssrc" && t.id !== "lssrc" && t.id !== "schedules") || isAdmin).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px rounded-t-lg border-b-2 px-4 py-2 text-sm font-medium transition ${tab === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <Card title="General / ExactOnline / Budgets / Approval / Schedules">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Setting</th><th>Description</th><th>Value</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {scalars.map((s) => <ScalarRow key={s.key} s={s} saving={setScalar.isPending} onSave={(key, value) => setScalar.mutate({ key, value })} />)}
            </tbody>
          </table>
        </Card>
      )}

      {tab === "vat" && (
        <Card title="VAT → SalesVatCode">
          <table className="w-full max-w-md text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">VAT %</th><th>SalesVatCode</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.vatCodeMap.map((v) => (
                <tr key={v.vatRate}><td className="py-2">{v.vatRate}%</td><td>{v.salesVatCode}</td><td><Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete VAT mapping ${v.vatRate}% → ${v.salesVatCode}?`)) vatDelete.mutate({ vatRate: v.vatRate }); }}>delete</Button></td></tr>
              ))}
              <tr>
                <td className="py-2"><Input value={newVat.vatRate} onChange={(e) => setNewVat({ ...newVat, vatRate: e.target.value })} placeholder="21" className="w-20" /></td>
                <td><Input value={newVat.salesVatCode} onChange={(e) => setNewVat({ ...newVat, salesVatCode: e.target.value })} placeholder="2" className="w-20" /></td>
                <td><Button variant="secondary" onClick={() => newVat.vatRate && vatUpsert.mutate({ vatRate: Number(newVat.vatRate), salesVatCode: Number(newVat.salesVatCode) })}>add</Button></td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {tab === "units" && (
        <Card title={`STUK units (${stuks.length}${stukSearch ? ` of ${data.stukUnits.length}` : ""})`} actions={<Input value={stukSearch} onChange={(e) => setStukSearch(e.target.value)} placeholder="search…" className="w-40" />}>
          <table className="w-full max-w-md text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Pick size</th><th>Unit code</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {stuks.map((u) => (
                <tr key={u.pickSize}><td className="py-2">{u.pickSize}{u.isSpecial && <Badge color="blue">special</Badge>}</td><td className="font-mono text-xs">{u.unitCode}</td><td><Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete STUK unit for pick size ${u.pickSize} (${u.unitCode})?`)) stukDelete.mutate({ pickSize: u.pickSize }); }}>delete</Button></td></tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex gap-2">
            <Input value={newStuk.pickSize} onChange={(e) => setNewStuk({ ...newStuk, pickSize: e.target.value })} placeholder="pick size" className="w-28" />
            <Input value={newStuk.unitCode} onChange={(e) => setNewStuk({ ...newStuk, unitCode: e.target.value })} placeholder="STUK/n" className="w-32" />
            <Button variant="secondary" onClick={() => newStuk.pickSize && stukUpsert.mutate({ pickSize: newStuk.pickSize, unitCode: newStuk.unitCode || `STUK/${newStuk.pickSize}` })}>add</Button>
          </div>
        </Card>
      )}

      {tab === "brands" && (
        <Card
          title={`Brand → ItemCode rule (${brands.length}${brandSearch || brandFiltersActive ? ` of ${allBrands.length}` : ""})`}
          actions={
            <div className="flex items-center gap-2">
              {(() => {
                const n = allBrands.filter(aBrandConflict).length;
                return n > 0 ? (
                  <button
                    onClick={() => { clearBrandFilters(); setFEconomic(["yes"]); setFABrand(["A-merk", "A-merk (handmatig)"]); }}
                    title="A-merken zijn normaliter niet Economic — klik om alleen deze te tonen"
                    className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-100"
                  >
                    {n} tegenstrijdig
                  </button>
                ) : null;
              })()}
              {brandFiltersActive && (
                <button onClick={clearBrandFilters} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
                  clear filters
                </button>
              )}
              <Input value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} placeholder="search…" className="w-40" />
              <Button variant="secondary" onClick={() => brandClassify.mutate()} disabled={brandClassify.isPending}>
                {brandClassify.isPending ? "Schatten…" : "A-merk schatten"}
              </Button>
              <Button variant="secondary" onClick={() => { if (confirm("Re-derive all brand rules from existing ExactOnline data? This overwrites current rows with the dominant observed pattern per brand.")) brandSeed.mutate(); }} disabled={brandSeed.isPending}>
                {brandSeed.isPending ? "Seeding…" : "Re-seed from data"}
              </Button>
            </div>
          }
        >
          <p className="mb-2 text-xs text-slate-500">
            New ItemCode = <span className="font-mono">prefix + (transform = reverse ? reverse(PBS&nbsp;code) : PBS&nbsp;code)</span>. <b>reverse</b> writes the PBS code back-to-front, character by character — <span className="font-mono">{SAMPLE_PBS_CODE}</span> becomes <span className="font-mono">{revCode(SAMPLE_PBS_CODE)}</span>; <b>identity</b> leaves it as it is. So a rule <span className="font-mono">BRE</span>&nbsp;+&nbsp;reverse turns PBS <span className="font-mono">{SAMPLE_PBS_CODE}</span> into ItemCode <span className="font-mono">BRE{revCode(SAMPLE_PBS_CODE)}</span>. It is not a choice we invented: ExactOnline has no single formula, so the dominant pattern per brand was mined from ~18k existing items — most brands reverse, some don't.
          </p>
          <p className="mb-2 text-xs text-slate-500">
            <b>A-merk?</b> and <b>kans</b> are an estimate, not a fact, and they change nothing on their own — Economic? stays yours to set. The percentage is the estimated probability that the brand belongs to a manufacturer rather than being a wholesaler's house label, weighed from four signals: whether the brand owns its GS1/EAN company prefix or shares it with a dozen other "brands", whether its markup is a fixed formula or priced per article, its price level against category peers, and known brand names. Hover a score to read the reasoning behind it. Press <span className="font-mono">A-merk schatten</span> to recompute (also available as <span className="font-mono">cli.js classify-brands</span>). <b>Disagree?</b> Click the verdict: once fixes it to A-merk, again to huismerk, a third time hands it back to the estimate. A fixed verdict wins over the score, survives every recompute, and is stamped with who set it and when.
          </p>
          <p className="mb-2 text-xs text-slate-500">
            One row per PBS-catalog brand (the create source). Brands with no rule fall back to the first {String(data.scalars.find((s) => s.key === "exact.fallbackPrefixLen")?.value ?? 3)} letters + {String(data.scalars.find((s) => s.key === "exact.fallbackTransform")?.value ?? "reverse")} (a shorter brand just yields a shorter prefix), flagged on the create preview. The <span className="font-mono">example</span> column shows each rule applied to the sample code <span className="font-mono">{SAMPLE_PBS_CODE}</span>, and follows along while you edit.
          </p>
          <table className="w-full max-w-3xl text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2"><ColumnFilter title="Brand" selected={fBrand} onChange={setFBrand} options={brandOptions((b) => b.brand).map((o) => ({ value: o.value, label: o.value }))} /></th>
                <th><ColumnFilter title="Prefix" selected={fPrefix} onChange={setFPrefix} options={brandOptions((b) => b.prefix).map((o) => ({ value: o.value, label: o.value || "(none)", count: o.count }))} /></th>
                <th><ColumnFilter title="Transform" selected={fTransform} onChange={setFTransform} options={brandOptions((b) => b.transform).map((o) => ({ value: o.value, label: o.value, count: o.count }))} /></th>
                <th className="font-normal normal-case tracking-normal text-slate-400">example ({SAMPLE_PBS_CODE} →)</th>
                <th><ColumnFilter title="Confidence" selected={fConfidence} onChange={setFConfidence} options={brandOptions((b) => confBand(b.confidence)).map((o) => ({ value: o.value, label: o.value, count: o.count }))} /></th>
                <th><ColumnFilter title="Economic?" selected={fEconomic} onChange={setFEconomic} options={brandOptions((b) => (b.isEconomic ? "yes" : "no")).map((o) => ({ value: o.value, label: o.value, count: o.count }))} /></th>
                <th><ColumnFilter title="A-merk?" selected={fABrand} onChange={setFABrand} options={brandOptions((b) => aBrandVerdict(b)).map((o) => ({ value: o.value, label: o.value, count: o.count }))} /></th>
                <th className="font-normal normal-case tracking-normal text-slate-400">kans</th>
                <th className="font-normal normal-case tracking-normal text-slate-400">handmatig door</th>
                <th></th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {brands.map((b) => {
                const editing = editBrand === b.brand;
                return (
                <tr key={b.brand} className={editing ? "bg-indigo-50/60" : undefined}>
                  <td className="py-2">{b.brand}</td>
                  <td className="font-mono text-xs">
                    {editing
                      ? <Input value={editDraft.prefix} onChange={(e) => setEditDraft({ ...editDraft, prefix: e.target.value })} className="w-24 font-mono text-xs" placeholder="prefix" />
                      : (b.prefix || <span className="text-slate-300">(none)</span>)}
                  </td>
                  <td>
                    {editing
                      ? <Select value={editDraft.transform} onChange={(e) => setEditDraft({ ...editDraft, transform: e.target.value as "identity" | "reverse" })}><option value="reverse">reverse</option><option value="identity">identity</option></Select>
                      : (
                        <span title={b.transform === "reverse"
                          ? `PBS code back-to-front: ${SAMPLE_PBS_CODE} → ${revCode(SAMPLE_PBS_CODE)}`
                          : `PBS code unchanged: ${SAMPLE_PBS_CODE} → ${SAMPLE_PBS_CODE}`}>
                          {b.transform === "reverse" ? <Badge color="blue">reverse</Badge> : <Badge color="gray">identity</Badge>}
                        </span>
                      )}
                  </td>
                  <td className="font-mono text-xs text-slate-500">
                    {editing
                      ? itemCodeExample(editDraft.prefix.trim(), editDraft.transform)
                      : itemCodeExample(b.prefix ?? "", b.transform)}
                  </td>
                  <td className="text-xs text-slate-500">{b.confidence != null ? `${b.confidence}% of ${b.sampleSize}` : "—"}</td>
                  <td>
                    <button
                      title={aBrandConflict(b)
                        ? "Tegenstrijdig: dit is een A-merk, en A-merken zijn normaliter niet Economic. Klik om Economic uit te zetten."
                        : b.isEconomic ? "Alternative/cheaper brand — click to unmark" : "Mark as alternative/cheaper brand"}
                      className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${aBrandConflict(b) ? "bg-red-100 text-red-800 ring-1 ring-red-300 hover:bg-red-200" : b.isEconomic ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                      onClick={() => editing
                        ? setEditDraft({ ...editDraft, isEconomic: !editDraft.isEconomic })
                        : brandUpsert.mutate({ brand: b.brand, prefix: b.prefix, transform: b.transform, isEconomic: !b.isEconomic })}
                    >
                      {(editing ? editDraft.isEconomic : b.isEconomic) ? "yes" : "no"}
                    </button>
                  </td>
                  <td title={b.aBrandManual != null ? "Handmatig vastgezet — klik om te wijzigen" : (b.aBrandReason ?? "Nog niet berekend — druk op 'A-merk schatten'.")}>
                    {/* Click cycles A-merk → huismerk → terug naar de schatting. Three states, one control:
                        the override only means something next to the estimate it replaces. */}
                    <button
                      onClick={() => aBrandManual.mutate({ brand: b.brand, verdict: b.aBrandManual === true ? false : b.aBrandManual === false ? null : true })}
                      disabled={aBrandManual.isPending}
                      className="disabled:opacity-50"
                    >
                      {b.aBrandManual === true ? <Badge color="green">A-merk ✓</Badge>
                        : b.aBrandManual === false ? <Badge color="amber">huismerk ✓</Badge>
                        : b.aBrandScore == null ? <span className="text-xs text-slate-300">—</span>
                        : b.aBrandScore >= 70 ? <Badge color="green">A-merk</Badge>
                        : b.aBrandScore <= 30 ? <Badge color="amber">huismerk</Badge>
                        : <Badge color="gray">onduidelijk</Badge>}
                    </button>
                  </td>
                  <td className="text-xs tabular-nums text-slate-500" title={b.aBrandReason ?? undefined}>
                    {b.aBrandScore == null ? "—" : <span className={b.aBrandManual != null ? "line-through opacity-50" : undefined}>{b.aBrandScore}%</span>}
                  </td>
                  <td className="text-[11px] text-slate-500">
                    {b.aBrandManual != null
                      ? <span title={`Handmatig vastgezet door ${b.aBrandManualBy ?? "onbekend"}`}>{b.aBrandManualBy ?? "onbekend"}<br /><span className="text-slate-400">{b.aBrandManualAt ? fmtDateTime(b.aBrandManualAt) : ""}</span></span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td><Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete the ItemCode rule for "${b.brand}"? New items for this brand will then use the fallback (${data.scalars.find((s) => s.key === "exact.fallbackPrefixLen")?.value ?? 3} letters + ${data.scalars.find((s) => s.key === "exact.fallbackTransform")?.value ?? "reverse"}).`)) brandDelete.mutate({ brand: b.brand }); }}>delete</Button></td>
                  <td>
                    {editing ? (
                      <div className="flex gap-1">
                        <Button size="sm" disabled={brandUpsert.isPending} onClick={() => { brandUpsert.mutate({ brand: b.brand, prefix: editDraft.prefix.trim(), transform: editDraft.transform, isEconomic: editDraft.isEconomic }); setEditBrand(null); }}>save</Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditBrand(null)}>cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => { setEditBrand(b.brand); setEditDraft({ prefix: b.prefix ?? "", transform: b.transform, isEconomic: !!b.isEconomic }); }}>modify</Button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input value={newBrand.brand} onChange={(e) => setNewBrand({ ...newBrand, brand: e.target.value })} placeholder="brand" className="w-40" />
            <Input value={newBrand.prefix} onChange={(e) => setNewBrand({ ...newBrand, prefix: e.target.value })} placeholder="prefix (e.g. BRE)" className="w-36" />
            <Select value={newBrand.transform} onChange={(e) => setNewBrand({ ...newBrand, transform: e.target.value as "identity" | "reverse" })}><option value="reverse">reverse</option><option value="identity">identity</option></Select>
            <label className="flex items-center gap-1.5 text-sm text-slate-600 select-none cursor-pointer">
              <input type="checkbox" checked={newBrand.isEconomic} onChange={(e) => setNewBrand({ ...newBrand, isEconomic: e.target.checked })} className="accent-amber-500" />
              Economic
            </label>
            <Button variant="secondary" disabled={!newBrand.brand.trim() || brandUpsert.isPending} onClick={() => newBrand.brand && brandUpsert.mutate({ brand: newBrand.brand.trim(), prefix: newBrand.prefix.trim(), transform: newBrand.transform, isEconomic: newBrand.isEconomic })}>
              {brandUpsert.isPending ? "Adding…" : "add"}
            </Button>
            <span className="self-center font-mono text-xs text-slate-400">{SAMPLE_PBS_CODE} → {itemCodeExample(newBrand.prefix.trim(), newBrand.transform)}</span>
            {brandUpsert.isError && <span className="self-center text-xs text-red-600">{brandUpsert.error.message}</span>}
            {brandUpsert.isSuccess && !brandUpsert.isPending && <span className="self-center text-xs text-emerald-600">saved ✓ (confidence computed from existing items)</span>}
          </div>
        </Card>
      )}

      {tab === "excluded" && (
        <div className="space-y-6">
        <Card title="Excluded categories">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <p className="max-w-3xl text-xs text-slate-500">
              Products in these categories are skipped for the chosen system. <span className="font-medium">pbs</span> excludes at the source — skipped in BOTH reconciles (ExactOnline and Lightspeed);{" "}
              <span className="font-medium">exact</span> skips only the PBS → ExactOnline sync; <span className="font-medium">lightspeed</span> matches a Lightspeed category id. Excluding a parent excludes everything under it.
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { window.location.href = "/api/export/exclusions"; }}
              title="Download every exclusion (categories, SKUs and keywords) as Excel, with the category path, product counts and what each exclusion blocks"
            >
              ⬇ Export Excel
            </Button>
          </div>

          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-700">
                <span className="font-semibold">Browse the PBS category tree</span> — tick whole branches instead of adding them one by one. Ticked categories are excluded at the source (system <span className="font-mono">pbs</span>).
              </div>
              <Button variant="secondary" size="sm" onClick={() => setTreeOpen(!treeOpen)}>
                {treeOpen ? "Hide tree" : "Select categories…"}
              </Button>
            </div>
            {treeOpen && <div className="mt-3"><PbsCategoryTreePicker onFlash={showFlash} /></div>}
          </div>
          <table className="w-full max-w-2xl text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">System</th><th>Category</th><th>ID</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.excludedCategories.map((e) => (
                <tr key={e.id}>
                  <td className="py-2"><Badge color={e.system === "lightspeed" ? "blue" : e.system === "exact" ? "green" : "gray"}>{e.system}</Badge></td>
                  <td>{e.label ?? <span className="text-slate-300">(name not resolved — re-add to fill)</span>}</td>
                  <td className="font-mono text-xs text-slate-500">{e.categoryId}</td>
                  <td><Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete excluded category ${e.system} / ${e.categoryId}${e.label ? ` (${e.label})` : ""}?`)) exclDelete.mutate({ id: e.id }); }}>delete</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-start gap-2">
            <Select value={newExcl.system} onChange={(e) => setNewExcl({ system: e.target.value as ExclSystem, categoryId: "" })}>
              <option value="exact">exact</option>
              <option value="pbs">pbs</option>
              <option value="lightspeed">lightspeed</option>
            </Select>
            <CategoryPicker
              system={newExcl.system}
              value={newExcl.categoryId}
              onChange={(v) => setNewExcl({ ...newExcl, categoryId: v })}
              onPick={(categoryId, label) => addExcl(categoryId, label ?? undefined)}
            />
            <Button variant="secondary" disabled={!newExcl.categoryId.trim() || exclUpsert.isPending} onClick={() => addExcl(newExcl.categoryId)}>
              {exclUpsert.isPending ? "Adding…" : "add"}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Pick from the list to add instantly, or type an id and click add — the category name is filled automatically.</p>

          {/* One-click: exclude all ink/toner/cartridge Lightspeed categories (owner directive). */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2">
            <div className="max-w-xl text-xs text-slate-700">
              <span className="font-semibold">Ink / toner / cartridge categories</span> — exclude every Lightspeed category named like “inkt”, “toner” or “cartridge” so those products are never synced or created on the shop. Uses the cached Lightspeed category list (run a Lightspeed snapshot first).
            </div>
            <Button variant="secondary" disabled={seedInkToner.isPending} onClick={() => seedInkToner.mutate()}>
              {seedInkToner.isPending ? "Scanning…" : "Exclude ink/toner categories"}
            </Button>
          </div>
        </Card>

        <Card title="Excluded SKUs">
          <p className="mb-3 text-xs text-slate-500">
            Exclude individual products by their <span className="font-medium">PBS SKU</span> for the chosen system. An excluded SKU is skipped in that system's reconcile (<span className="font-medium">exact</span> = skip PBS → ExactOnline; <span className="font-medium">lightspeed</span> = skip ExactOnline → Lightspeed). The product name is filled in automatically.
          </p>

          {/* One-click: detect the 6122012-style drop-ship loss-risk items and exclude them. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
            <div className="max-w-xl text-xs text-amber-900">
              <span className="font-semibold">Drop-ship loss risk</span> — find products like <span className="font-mono">6122012</span> that are <span className="font-medium">drop-shipped</span> but ordered/sold per piece while PBS ships only by the pack (we'd ship more than we charged for) and exclude them. Reads the latest ExactOnline snapshot.
            </div>
            <Button variant="secondary" disabled={seedLossRisk.isPending} onClick={() => seedLossRisk.mutate({ system: "lightspeed" })}>
              {seedLossRisk.isPending ? "Scanning…" : "Find & exclude from Lightspeed"}
            </Button>
          </div>

          {skuRows.length > 0 ? (
            <table className="w-full max-w-2xl text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">System</th><th>Product</th><th>SKU</th><th></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {skuRows.map((e) => (
                  <tr key={`${e.system}:${e.sku}`}>
                    <td className="py-2"><Badge color={e.system === "lightspeed" ? "blue" : e.system === "exact" ? "green" : "gray"}>{e.system}</Badge></td>
                    <td>{e.label ?? <span className="text-slate-300">(name not resolved)</span>}</td>
                    <td className="font-mono text-xs text-slate-500">{e.sku}</td>
                    <td><Button variant="danger" size="sm" onClick={() => { if (confirm(`Remove SKU exclusion ${e.system} / ${e.sku}${e.label ? ` (${e.label})` : ""}?`)) exclSkuDelete.mutate({ system: e.system, sku: e.sku }); }}>delete</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400">No SKU exclusions yet.</p>
          )}
          <div className="mt-3 flex flex-wrap items-start gap-2">
            <Select value={newExclSku.system} onChange={(e) => setNewExclSku({ system: e.target.value as ExclSystem, sku: newExclSku.sku })}>
              <option value="exact">exact</option>
              <option value="pbs">pbs</option>
              <option value="lightspeed">lightspeed</option>
            </Select>
            <Input
              value={newExclSku.sku}
              onChange={(e) => setNewExclSku({ ...newExclSku, sku: e.target.value })}
              placeholder="PBS SKU (e.g. 072339)"
              className="w-72"
            />
            <Button variant="secondary" disabled={!newExclSku.sku.trim() || exclSkuUpsert.isPending} onClick={addExclSku}>
              {exclSkuUpsert.isPending ? "Adding…" : "add"}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Type a PBS SKU and click add — the product name is filled automatically. The exclusion takes effect on the next reconcile for that system.</p>
        </Card>

        <Card title="Excluded by name — keywords">
          <p className="mb-3 text-xs text-slate-500">
            Products whose <span className="font-medium">name</span> contains any of these words are skipped for the chosen system:{" "}
            <span className="font-medium">lightspeed</span> = never created on the webshop (and any already there are left unmanaged);{" "}
            <span className="font-medium">exact</span> = skipped in the PBS → ExactOnline sync (not created/updated). Case-insensitive, matches
            anywhere in the name. Typical use: <span className="font-mono">statiegeld</span> (the deposit on cans/bottles).
          </p>
          {(["lightspeed", "exact"] as const).map((sys) => {
            const kws = data.excludedTitleKeywords?.[sys] ?? [];
            return (
              <div key={sys} className="mt-3">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <Badge color={sys === "lightspeed" ? "blue" : "green"}>{sys}</Badge>
                  <span className="text-[11px] text-slate-400">
                    {sys === "lightspeed"
                      ? "— never created on the webshop; any already on Lightspeed are left untouched (no price/visibility changes). Does NOT remove or hide existing products."
                      : "— skipped in the PBS → ExactOnline sync: not created and not updated in ExactOnline. Does NOT delete or deactivate an item that already exists."}
                  </span>
                </div>
                {kws.length ? (
                  <div className="flex flex-wrap gap-2">
                    {kws.map((kw) => (
                      <span key={kw} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700 ring-1 ring-inset ring-slate-200">
                        {kw}
                        <button
                          type="button"
                          className="leading-none text-slate-400 transition hover:text-red-600 disabled:opacity-50"
                          title={`Remove “${kw}” from ${sys}`}
                          disabled={keywordRemove.isPending}
                          onClick={() => { if (confirm(`Stop excluding “${kw}” for ${sys}?`)) keywordRemove.mutate({ keyword: kw, system: sys }); }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">None.</p>
                )}
              </div>
            );
          })}
          <div className="mt-4 flex flex-wrap items-start gap-2">
            <Select value={newKeywordSystem} onChange={(e) => setNewKeywordSystem(e.target.value as "lightspeed" | "exact" | "both")}>
              <option value="lightspeed">Lightspeed</option>
              <option value="exact">ExactOnline</option>
              <option value="both">Both</option>
            </Select>
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addKeyword(); }}
              placeholder="keyword (e.g. statiegeld)"
              className="w-72"
            />
            <Button variant="secondary" disabled={!newKeyword.trim() || keywordAdd.isPending} onClick={addKeyword}>
              {keywordAdd.isPending ? "Adding…" : "add"}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Takes effect on the next reconcile for the chosen system.</p>
        </Card>
        </div>
      )}

      {tab === "mapping" && (
        <div className="space-y-5">
          {/* sub-tabs: categories vs brands, with live unmapped counts */}
          <div className="flex w-fit rounded-lg border border-slate-300 bg-white p-0.5">
            <button onClick={() => setMapSub("categories")} className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${mapSub === "categories" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Categories{catMapList.data ? ` · ${catMapList.data.unmappedCount} unmapped` : ""}</button>
            <button onClick={() => setMapSub("brands")} className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${mapSub === "brands" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Brands{brandMapList.data ? ` · ${brandMapList.data.unmappedCount} unmapped` : ""}</button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input placeholder={`search ${mapSub === "categories" ? "category" : "brand"}…`} value={mapQ} onChange={(e) => setMapQ(e.target.value)} className="w-64" />
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={mapUnmapped} onChange={(e) => setMapUnmapped(e.target.checked)} className="accent-indigo-600" /> only unmapped
            </label>
            {mapSub === "categories" ? (
              <Button variant="secondary" disabled={catAutoMap.isPending} onClick={() => catAutoMap.mutate()} title="Map every category whose name exactly matches a Lightspeed category">{catAutoMap.isPending ? "Auto-mapping…" : "⚡ Auto-map exact matches"}</Button>
            ) : (
              <Button variant="secondary" disabled={brandAutoMap.isPending} onClick={() => brandAutoMap.mutate()} title="Map every brand whose name exactly matches a Lightspeed brand">{brandAutoMap.isPending ? "Auto-mapping…" : "⚡ Auto-map exact matches"}</Button>
            )}
          </div>

          {/* Sorting + filtering */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
            <label className="flex items-center gap-1.5">
              Sort
              {mapSub === "categories" ? (
                <Select value={catSort} onChange={(e) => setCatSort(e.target.value as typeof catSort)} className="py-1.5">
                  <option value="products">Most products</option>
                  <option value="breadcrumb">Breadcrumb A→Z</option>
                  <option value="score-desc">Best suggestion first</option>
                  <option value="score-asc">Weakest suggestion first</option>
                  <option value="mapping-asc">Mapping quality ↑ (worst first)</option>
                  <option value="mapping-desc">Mapping quality ↓ (best first)</option>
                </Select>
              ) : (
                <Select value={brandSort} onChange={(e) => setBrandSort(e.target.value as typeof brandSort)} className="py-1.5">
                  <option value="products">Most products</option>
                  <option value="name">Name A→Z</option>
                  <option value="score-desc">Best match first</option>
                  <option value="score-asc">Weakest match first</option>
                </Select>
              )}
            </label>
            {mapSub === "categories" && (
              <label className="flex items-center gap-1.5">
                Branch
                <Select value={mapBranch} onChange={(e) => setMapBranch(e.target.value)} className="py-1.5">
                  <option value="">All branches</option>
                  {(catMapList.data?.branches ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
                </Select>
              </label>
            )}
            <label className="flex items-center gap-1.5">
              Match
              <Select value={mapMatch} onChange={(e) => setMapMatch(e.target.value as typeof mapMatch)} className="py-1.5">
                <option value="all">Any suggestion</option>
                <option value="strong">Strong (≥70%)</option>
                <option value="weak">Weak (&lt;70%)</option>
                <option value="none">No suggestion</option>
              </Select>
            </label>
            <button
              onClick={() => setMapCompact((v) => !v)}
              className={`rounded border px-2.5 py-1 text-xs font-medium transition ${mapCompact ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
              title="Toggle compact rows — show more at once"
            >
              {mapCompact ? "Compact ✓" : "Compact"}
            </button>
          </div>

          {/* Bulk "assign to selected" — appears when rows are checked */}
          {selCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
              <span className="text-sm font-medium text-indigo-900">{selCount.toLocaleString()} selected</span>
              {matchingCount > selCount && (
                <button onClick={selectAllMatching} className="text-xs font-medium text-indigo-600 hover:underline">select all {matchingCount.toLocaleString()} matching</button>
              )}
              <span className="text-sm text-slate-600">→ assign to</span>
              {bulkTarget ? (
                <span className="inline-flex items-center gap-1"><Badge color="blue">{bulkTarget.lsTitle}</Badge><button onClick={() => setBulkTarget(null)} className="text-slate-400 hover:text-red-600" title="clear target">×</button></span>
              ) : (
                <LsPicker kind={mapSub === "categories" ? "category" : "brand"} onPick={(id, t) => setBulkTarget({ lsId: id, lsTitle: t })} placeholder={`pick LS ${mapSub === "categories" ? "category" : "brand"}…`} />
              )}
              <Button size="sm" disabled={!bulkTarget || bulkBusy} onClick={assignBulk}>{catBulk.isPending || brandBulk.isPending ? "Assigning…" : "Assign to selected"}</Button>
              {mapSub === "categories" && (
                <>
                  <span className="text-slate-300">|</span>
                  <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={excludeBulk} title="Hide these from Mapping and skip them in the PBS → ExactOnline sync (reversible in Exclusions)">{catExclude.isPending ? "Excluding…" : "Exclude selected"}</Button>
                </>
              )}
              <button onClick={() => (mapSub === "categories" ? setSelCats(new Set()) : setSelBrands(new Set()))} className="text-xs text-slate-500 hover:underline">clear selection</button>
              {mapSub === "categories" && (
                <div className="mt-1 flex w-full flex-wrap items-center gap-3 border-t border-indigo-200 pt-2">
                  <span className="text-xs font-medium text-indigo-800">+ extra category for selected:</span>
                  {bulkExtraTarget ? (
                    <span className="inline-flex items-center gap-1"><Badge color="blue">{bulkExtraTarget.lsTitle}</Badge><button onClick={() => setBulkExtraTarget(null)} className="text-slate-400 hover:text-red-600" title="clear">×</button></span>
                  ) : (
                    <LsPicker kind="category" onPick={(id, t) => setBulkExtraTarget({ lsId: id, lsTitle: t })} placeholder="pick extra LS category…" />
                  )}
                  <Button
                    size="sm"
                    disabled={!bulkExtraTarget || catExtraBulkAdd.isPending}
                    onClick={() => {
                      if (!bulkExtraTarget) return;
                      const ids = [...selCats];
                      if (ids.length && confirm(`Add extra LS category "${bulkExtraTarget.lsTitle}" to ${ids.length} selected PBS categor${ids.length === 1 ? "y" : "ies"}?`)) {
                        catExtraBulkAdd.mutate({ pbsCategoryIds: ids, lsId: bulkExtraTarget.lsId, lsTitle: bulkExtraTarget.lsTitle });
                      }
                    }}
                  >
                    {catExtraBulkAdd.isPending ? "Adding…" : "Add extra to selected"}
                  </Button>
                </div>
              )}
            </div>
          )}
          {/* Capped render — tell the user the rest are still reachable via "select all N matching" */}
          {renderedCount < matchingCount && (
            <p className="text-xs text-slate-500">
              Showing the first {renderedCount.toLocaleString()} of {matchingCount.toLocaleString()} matching. Tick the header checkbox, then{" "}
              <button onClick={selectAllMatching} className="font-medium text-indigo-600 hover:underline">select all {matchingCount.toLocaleString()} matching</button> to bulk-assign every match.
            </p>
          )}

          {mapSub === "categories" && (
          <Card title="Category mapping — PBS → Lightspeed">
            <p className="mb-3 text-xs text-slate-500">
              Products are created on the webshop in their mapped Lightspeed category; unmapped ones are held back. Accept a suggestion, pick a category, or create a new one in Lightspeed. Suggestions are scored by overlapping breadcrumb segments (the path after the root) and show a % match — sort or filter by it above, or tick rows to bulk-assign one category to many.
              {catMapList.data && <> <span className="font-semibold text-amber-700">{catMapList.data.unmappedCount}</span> of {catMapList.data.total} in-stock categories still need mapping · LS categories cached: {catMapList.data.lsCategoryCount} (run a Lightspeed snapshot to refresh).{catMapList.data.excludedHidden > 0 && <> Excluded categories ({catMapList.data.excludedHidden}) are hidden.</>}</>}
            </p>
            {isAdmin && (
              <div className="mb-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                <p className="text-xs text-amber-800">
                  Existing Lightspeed products are missing extra categories. Preview first to see the scale, then run.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" variant="secondary" disabled={extraCatsPreviewMut.isPending} onClick={() => { setExtraCatsPreviewResult(null); extraCatsPreviewMut.mutate(); }}>
                    {extraCatsPreviewMut.isPending ? "Calculating…" : "Preview"}
                  </Button>
                  {extraCatsPreviewResult && (
                    <>
                      <span className="text-xs text-amber-900 font-medium">
                        {extraCatsPreviewResult.products} products · {extraCatsPreviewResult.linkActions} link actions expected
                        {extraCatsPreviewResult.linkActions === 0 && " — nothing to do"}
                      </span>
                      {extraCatsPreviewResult.linkActions > 0 && (
                        <Button
                          size="sm"
                          disabled={extraCatsApplyMut.isPending}
                          onClick={() => {
                            if (confirm(`Run ${extraCatsPreviewResult.linkActions} link actions on Lightspeed (existing links are skipped)?\n\nRequires WRITE_MODE=live. This starts a background task.`)) {
                              extraCatsApplyMut.mutate();
                            }
                          }}
                        >
                          {extraCatsApplyMut.isPending ? "Starting…" : `Run (${extraCatsPreviewResult.linkActions} actions)`}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {catMapList.isLoading ? <Spinner /> : !catMapList.data?.items.length ? <p className="text-sm text-slate-400">{mapQ || mapBranch || mapMatch !== "all" || mapUnmapped ? "No categories match the current filters." : "Nothing to map."}</p> : (
              <div className="overflow-x-auto">
              <table className={`w-full text-left ${mapCompact ? "text-xs [&>tbody>tr>td]:py-0.5" : "text-sm"}`}>
                <thead className="sticky top-0 z-10 bg-white text-xs uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_theme(colors.slate.200)]">
                  <tr>
                    <th className="w-8 py-2"><input type="checkbox" checked={catIds.length > 0 && catIds.every((id) => selCats.has(id))} onChange={() => toggleAll(catIds, selCats, setSelCats)} className="accent-indigo-600" aria-label="select all categories" /></th>
                    <th className="py-2">PBS category</th>
                    <th className="py-2">Products</th>
                    <th className="py-2">Lightspeed category</th>
                    <th className="py-2">Extra categories</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {catMapList.data.items.map((it) => (
                    <tr key={it.pbsId} className={`align-top ${selCats.has(it.pbsId) ? "bg-indigo-50/50" : it.mapped ? "bg-emerald-50/40" : ""}`}>
                      <td className="py-2 pr-2"><input type="checkbox" checked={selCats.has(it.pbsId)} onClick={(e) => selectRow(catIds, it.pbsId, e.shiftKey, selCats, setSelCats)} onChange={() => {}} className="accent-indigo-600" aria-label={`select ${it.pbsName}`} /></td>
                      <td className="py-2 pr-3 max-w-[220px]"><div className="text-slate-800 break-words">{it.pbsPath || it.pbsName || <span className="text-slate-400">(no name)</span>}</div><div className="font-mono text-[11px] text-slate-400">{it.pbsId}</div></td>
                      <td className="py-2 pr-3 text-slate-500"><Link href={`/products?category=${encodeURIComponent(it.pbsId)}&catname=${encodeURIComponent(it.pbsName)}`} className="text-indigo-600 hover:underline" title="View these products on the Products page">{it.productCount}</Link></td>
                      <td className="py-2 pr-3">
                        <MapCell
                          kind="category"
                          mapped={it.mapped}
                          suggestion={it.suggestion}
                          mappingScore={it.mappingScore}
                          defaultName={it.pbsName}
                          busy={(catCreate.isPending && catCreate.variables?.pbsCategoryId === it.pbsId) || (catMapUpsert.isPending && catMapUpsert.variables?.pbsCategoryId === it.pbsId)}
                          onPick={(lsId, lsTitle) => catMapUpsert.mutate({ pbsCategoryId: it.pbsId, lsId, lsTitle })}
                          onRemove={() => catMapDelete.mutate({ pbsCategoryId: it.pbsId })}
                          onCreate={(title, parentLsId) => catCreate.mutate({ pbsCategoryId: it.pbsId, title, parentLsId: parentLsId ?? undefined })}
                        />
                      </td>
                      <td className="py-2">
                        <ExtraCatCell
                          pbsId={it.pbsId}
                          extras={catExtraMap.data?.[it.pbsId] ?? []}
                          onAdd={(lsId, lsTitle) => catExtraUpsert.mutate({ pbsCategoryId: it.pbsId, lsId, lsTitle })}
                          onRemove={(lsId) => catExtraDelete.mutate({ pbsCategoryId: it.pbsId, lsId })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Card>
          )}

          {mapSub === "brands" && (
          <Card title="Brand mapping — PBS → Lightspeed">
            <p className="mb-3 text-xs text-slate-500">
              Brands are <span className="font-medium">auto-created</span> on Lightspeed when a product needs one — map here only to override the match.
              {brandMapList.data && <> {brandMapList.data.unmappedCount} of {brandMapList.data.total} brands unmapped · LS brands cached: {brandMapList.data.lsBrandCount}.</>}
            </p>
            {brandMapList.isLoading ? <Spinner /> : !brandMapList.data?.items.length ? <p className="text-sm text-slate-400">{mapQ || mapMatch !== "all" || mapUnmapped ? "No brands match the current filters." : "Nothing to map."}</p> : (
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="w-8 py-2"><input type="checkbox" checked={brandIds.length > 0 && brandIds.every((id) => selBrands.has(id))} onChange={() => toggleAll(brandIds, selBrands, setSelBrands)} className="accent-indigo-600" aria-label="select all brands" /></th><th>PBS brand</th><th>Products</th><th>Lightspeed brand</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {brandMapList.data.items.map((it) => (
                    <tr key={it.pbsBrand} className={`align-top ${selBrands.has(it.pbsBrand) ? "bg-indigo-50/50" : it.mapped ? "bg-emerald-50/40" : ""}`}>
                      <td className="py-2"><input type="checkbox" checked={selBrands.has(it.pbsBrand)} onClick={(e) => selectRow(brandIds, it.pbsBrand, e.shiftKey, selBrands, setSelBrands)} onChange={() => {}} className="accent-indigo-600" aria-label={`select ${it.pbsBrand}`} /></td>
                      <td className="py-2 text-slate-800">{it.pbsBrand}</td>
                      <td className="text-slate-500">{it.productCount}</td>
                      <td>
                        <MapCell
                          kind="brand"
                          mapped={it.mapped}
                          suggestion={it.suggestion}
                          busy={brandMapUpsert.isPending && brandMapUpsert.variables?.pbsBrand === it.pbsBrand}
                          onPick={(lsId, lsTitle) => brandMapUpsert.mutate({ pbsBrand: it.pbsBrand, lsId, lsTitle })}
                          onRemove={() => brandMapDelete.mutate({ pbsBrand: it.pbsBrand })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          )}
        </div>
      )}

      {tab === "exactsrc" && isAdmin && (
        <div className="space-y-5">
          <Card title="ExactOnline data — primary source">
            <p className="mb-3 text-xs text-slate-500">
              Choose where the app <span className="font-medium">reads</span> ExactOnline data (the snapshot used by reconcile and the dashboards). The
              other source is used <span className="font-medium">automatically as a fallback</span> if the primary is unavailable. <span className="font-medium">Writes</span> (creating/updating items &amp; prices) and the safety checks right before/after a write <span className="font-medium">always go through MCP</span>, never the replica.
            </p>
            <div className="flex w-fit rounded-lg border border-slate-300 bg-white p-0.5">
              {(["replica", "mcp"] as const).map((s) => {
                const active = exactSrc.data?.readSource === s;
                return (
                  <button
                    key={s}
                    disabled={setExactSrc.isPending}
                    onClick={() => { if (!active) setExactSrc.mutate({ source: s }); }}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${active ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    {s === "replica" ? "Replica (MSSQL)" : "ExactOnline via MCP"}
                  </button>
                );
              })}
            </div>
            {exactSrc.data && (
              <p className="mt-2 text-[11px] text-slate-400">
                Primary: <span className="font-medium text-slate-600">{exactSrc.data.readSource === "replica" ? "Replica (MSSQL)" : "ExactOnline via MCP"}</span> · fallback: {exactSrc.data.readSource === "replica" ? "ExactOnline via MCP" : "Replica (MSSQL)"}
                {exactSrc.data.readSource === "replica" && !exactSrc.data.replica.hasPassword && <span className="ml-1 font-medium text-amber-600">— replica not configured yet, so MCP is used.</span>}
              </p>
            )}
          </Card>

          <Card
            title="ExactOnline / MCP connection"
            actions={
              <div className="flex items-center gap-2">
                {exactSrc.data?.mcp && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${exactSrc.data.mcp.hasKey ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${exactSrc.data.mcp.hasKey ? "bg-emerald-500" : "bg-amber-500"}`} /> {exactSrc.data.mcp.hasKey ? "key set" : "no key"}
                  </span>
                )}
                <Button variant="secondary" disabled={mcpTest.isPending} onClick={() => { setMcpTestResult(null); mcpTest.mutate(); }}>{mcpTest.isPending ? "Testing…" : "Test connection"}</Button>
              </div>
            }
          >
            <p className="mb-3 text-xs text-slate-500">
              All ExactOnline reads/writes go through the <span className="font-medium">MCP proxy</span>. The API key is stored server-side and shown here (admin only) — use <span className="font-medium">Hide</span> to mask it.
              Leave a field blank to fall back to the server's environment value{exactSrc.data?.mcp?.usingEnvUrl || exactSrc.data?.mcp?.usingEnvKey ? <span className="text-slate-400"> (currently using the env {[exactSrc.data?.mcp?.usingEnvUrl ? "URL" : null, exactSrc.data?.mcp?.usingEnvKey ? "key" : null].filter(Boolean).join(" + ")})</span> : null}.
            </p>
            <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:col-span-2">MCP URL
                <Input value={mcp.url} onChange={(e) => setMcp({ ...mcp, url: e.target.value })} placeholder="https://exactonline-proxy.ecotone.eu/api/mcp" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:col-span-2">
                <span className="flex items-center justify-between">MCP API key
                  <button type="button" onClick={() => setShowSecrets((v) => !v)} className="normal-case text-[11px] font-normal text-indigo-600 hover:underline">{showSecrets ? "Hide" : "Show"}</button>
                </span>
                <Input type={showSecrets ? "text" : "password"} value={mcp.key} onChange={(e) => setMcp({ ...mcp, key: e.target.value })} placeholder="X-Api-Key" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                <span className="flex items-center justify-between">ExactOnline division
                  <button type="button" disabled={mcpDivisions.isPending} onClick={() => mcpDivisions.mutate()} className="normal-case text-[11px] font-normal text-indigo-600 hover:underline disabled:text-slate-300">{mcpDivisions.isPending ? "Loading…" : "Load from key"}</button>
                </span>
                {divisions && divisions.length ? (
                  <select value={mcp.division} onChange={(e) => setMcp({ ...mcp, division: e.target.value })} className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {mcp.division && !divisions.some((d) => String(d.code) === mcp.division) && <option value={mcp.division}>{mcp.division} (current)</option>}
                    {divisions.map((d) => <option key={d.code} value={String(d.code)}>{d.code}{d.description ? ` — ${d.description}` : ""}{d.current ? " ✓" : ""}</option>)}
                  </select>
                ) : (
                  <Input value={mcp.division} onChange={(e) => setMcp({ ...mcp, division: e.target.value.replace(/[^0-9]/g, "") })} placeholder="937509" className="w-40" />
                )}
                <span className="mt-0.5 text-[10px] normal-case text-amber-600">Company/administration number — used on every ExactOnline call. “Load from key” lists the divisions this MCP key can access.</span>
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">PBS supplier code
                <Input value={mcp.supplierCode} onChange={(e) => setMcp({ ...mcp, supplierCode: e.target.value })} placeholder="87577578" className="w-40" />
                <span className="mt-0.5 text-[10px] normal-case text-slate-400">Matches PBS ↔ ExactOnline SupplierItems.</span>
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:col-span-2">ExactOnline item URL base (for “view in ExactOnline” links)
                <Input value={mcp.itemUrlBase} onChange={(e) => setMcp({ ...mcp, itemUrlBase: e.target.value })} placeholder="https://start.exactonline.nl/docs/LogItemCard.aspx" />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                disabled={mcpSave.isPending}
                onClick={() => {
                  const divNum = Number(mcp.division) || 0;
                  const divChanged = String(divNum) !== String(exactSrc.data?.mcp?.division ?? "");
                  if (divChanged && !confirm(`Change the ExactOnline division to ${divNum}? This is used on every ExactOnline call — a wrong value breaks all ExactOnline reads/writes.`)) return;
                  mcpSave.mutate({ url: mcp.url.trim(), key: mcp.key || undefined, division: divNum, itemUrlBase: mcp.itemUrlBase.trim(), supplierCode: mcp.supplierCode.trim() });
                }}
              >
                {mcpSave.isPending ? "Saving…" : "Save ExactOnline / MCP settings"}
              </Button>
              {mcpTestResult && (
                <span className={`inline-flex items-center gap-1.5 text-sm ${mcpTestResult.ok ? "text-emerald-700" : "text-red-600"}`}>
                  {mcpTestResult.ok ? "✓ Connected" : "✕ Failed"} — {mcpTestResult.message}
                </span>
              )}
            </div>
          </Card>

          <Card title="Verify ExactOnline writes (read-back)">
            <p className="mb-3 text-xs text-slate-500">
              After each ExactOnline update the app reads the record back to confirm the change actually took effect (an ExactOnline PUT returns no
              body, so this is the only confirmation). It's the safe default, but it costs <span className="font-medium">one extra API call per update</span> —
              noticeable on big change sets. Turn it off to apply faster and cheaper, trusting ExactOnline's success response. Lightspeed already
              verifies from the write response (no extra call), so this only affects ExactOnline updates.
            </p>
            <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={exactSrc.data?.verifyWrites ?? true}
                disabled={setVerifyWrites.isPending || !exactSrc.data}
                onChange={(e) => setVerifyWrites.mutate({ on: e.target.checked })}
              />
              <span className="font-medium text-slate-700">Verify ExactOnline writes with a read-back</span>
              <span className="text-slate-400">{(exactSrc.data?.verifyWrites ?? true) ? "(on — safest)" : "(off — faster/cheaper)"}</span>
            </label>
          </Card>

          <Card
            title="Replica connection (read-only MSSQL)"
            actions={
              <div className="flex items-center gap-2">
                <Button variant="secondary" disabled={replTest.isPending} onClick={() => { setReplTestResult(null); replTest.mutate(); }}>{replTest.isPending ? "Testing…" : "Test connection"}</Button>
              </div>
            }
          >
            <p className="mb-3 text-xs text-slate-500">Credentials are stored server-side; they're shown here (admin only) so you can view and edit them — use <span className="font-medium">Hide</span> to mask. Save first, then test. The user only needs <span className="font-medium">read</span> access to the <span className="font-mono">_V2</span> tables.</p>
            <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Server / host
                <Input value={repl.server} onChange={(e) => setRepl({ ...repl, server: e.target.value })} placeholder="sql.host.local or 10.0.0.5" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Port
                <Input value={repl.port} onChange={(e) => setRepl({ ...repl, port: e.target.value.replace(/[^0-9]/g, "") })} placeholder="1433" className="w-28" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Database
                <Input value={repl.database} onChange={(e) => setRepl({ ...repl, database: e.target.value })} placeholder="ExactReplica" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">User
                <Input value={repl.user} onChange={(e) => setRepl({ ...repl, user: e.target.value })} placeholder="readonly_user" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                <span className="flex items-center justify-between">Password
                  <button type="button" onClick={() => setShowSecrets((v) => !v)} className="normal-case text-[11px] font-normal text-indigo-600 hover:underline">{showSecrets ? "Hide" : "Show"}</button>
                </span>
                <Input type={showSecrets ? "text" : "password"} value={repl.password} onChange={(e) => setRepl({ ...repl, password: e.target.value })} placeholder="password" />
              </label>
              <div className="flex items-end gap-4 text-sm text-slate-600">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={repl.encrypt} onChange={(e) => setRepl({ ...repl, encrypt: e.target.checked })} className="accent-indigo-600" /> Encrypt</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={repl.trustServerCertificate} onChange={(e) => setRepl({ ...repl, trustServerCertificate: e.target.checked })} className="accent-indigo-600" /> Trust server cert</label>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button disabled={replSave.isPending || !repl.server.trim() || !repl.database.trim() || !repl.user.trim()} onClick={() => replSave.mutate({ server: repl.server.trim(), port: Number(repl.port) || 1433, database: repl.database.trim(), user: repl.user.trim(), password: repl.password || undefined, encrypt: repl.encrypt, trustServerCertificate: repl.trustServerCertificate })}>
                {replSave.isPending ? "Saving…" : "Save replica settings"}
              </Button>
              {replTestResult && (
                <span className={`inline-flex items-center gap-1.5 text-sm ${replTestResult.ok ? "text-emerald-700" : "text-red-600"}`}>
                  {replTestResult.ok ? "✓ Connected" : "✕ Failed"} — {replTestResult.ok && replTestResult.counts
                    ? `Items ${replTestResult.counts.items.toLocaleString()} · PBS supplier items ${replTestResult.counts.supplierItems.toLocaleString()} · active prices ${replTestResult.counts.activeSalesPrices.toLocaleString()}`
                    : replTestResult.message}
                </span>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === "lssrc" && isAdmin && (
        <div className="space-y-5">
          <Card
            title="Lightspeed API credentials"
            actions={
              <div className="flex items-center gap-2">
                {lsSrc.data && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${lsSrc.data.keyCount > 1 ? "bg-emerald-50 text-emerald-700" : lsSrc.data.hasSecret ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${lsSrc.data.keyCount > 1 ? "bg-emerald-500" : lsSrc.data.hasSecret ? "bg-slate-400" : "bg-amber-500"}`} />
                    {lsSrc.data.keyCount === 0 ? "no key" : lsSrc.data.keyCount === 1 ? "1 key" : "2 keys — alternating"}
                  </span>
                )}
                <Button variant="secondary" disabled={lsTest.isPending} onClick={() => { setLsTestResult(null); lsTest.mutate(); }}>{lsTest.isPending ? "Testing…" : "Test connection"}</Button>
              </div>
            }
          >
            <p className="mb-3 max-w-3xl text-xs text-slate-500">
              The webshop connection. Stored in the database and editable here, so it can be changed without access to the server's <span className="font-mono">.env</span> — leave a field blank to fall back to the environment value.
              A <span className="font-medium">second key pair is optional</span>: Lightspeed meters its rate limit per API key, so with two keys the app alternates between them and gets double the headroom on a big apply. That is what the 429 errors on a large run are about.
            </p>

            <div className="grid max-w-3xl gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 text-xs font-semibold text-slate-700">Key 1 (primary)</div>
                <label className="block text-[11px] text-slate-500">API key</label>
                <Input value={ls.key} onChange={(e) => setLs({ ...ls, key: e.target.value })} className="mb-2 w-full font-mono text-xs" placeholder={lsSrc.data?.envKey ? `from .env: ${lsSrc.data.envKey}` : "API key"} />
                <label className="block text-[11px] text-slate-500">API secret</label>
                <Input type={showLsSecret ? "text" : "password"} value={ls.secret} onChange={(e) => setLs({ ...ls, secret: e.target.value })} className="w-full font-mono text-xs" placeholder="leave blank to keep the stored secret" />
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 text-xs font-semibold text-slate-700">Key 2 (optional — doubles the rate limit)</div>
                <label className="block text-[11px] text-slate-500">API key</label>
                <Input value={ls.key2} onChange={(e) => setLs({ ...ls, key2: e.target.value })} className="mb-2 w-full font-mono text-xs" placeholder="second API key" />
                <label className="block text-[11px] text-slate-500">API secret</label>
                <Input type={showLsSecret ? "text" : "password"} value={ls.secret2} onChange={(e) => setLs({ ...ls, secret2: e.target.value })} className="w-full font-mono text-xs" placeholder="second API secret" />
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 text-xs font-semibold text-slate-700">Key 3 (optional — trebles it)</div>
                <label className="block text-[11px] text-slate-500">API key</label>
                <Input value={ls.key3} onChange={(e) => setLs({ ...ls, key3: e.target.value })} className="mb-2 w-full font-mono text-xs" placeholder="third API key" />
                <label className="block text-[11px] text-slate-500">API secret</label>
                <Input type={showLsSecret ? "text" : "password"} value={ls.secret3} onChange={(e) => setLs({ ...ls, secret3: e.target.value })} className="w-full font-mono text-xs" placeholder="third API secret" />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Lightspeed telt per sleutel: 300 per 5 minuten, 3.000 per uur, 12.000 per dag. De app wisselt om de beurt, dus elke extra
              sleutel telt er echt bij op. Zet <span className="font-mono">budget.lsPerDay</span> mee omhoog, anders knijpt de app zichzelf af
              op de oude waarde — en houd bewust wat over voor de rest van de dag.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button disabled={lsSave.isPending} onClick={() => lsSave.mutate({ key: ls.key.trim(), secret: ls.secret, key2: ls.key2.trim(), secret2: ls.secret2, key3: ls.key3.trim(), secret3: ls.secret3 })}>
                {lsSave.isPending ? "Saving…" : "Save"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowLsSecret(!showLsSecret)}>{showLsSecret ? "Hide secrets" : "Show secrets"}</Button>
              <span className="text-[11px] text-slate-400">API base: <span className="font-mono">{lsSrc.data?.apiUrl}</span></span>
            </div>

            {/* Live headroom straight from Lightspeed, per key — the number that decides whether a big
                apply will hit "429 after 6 retries" halfway through. */}
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-700">Rate limit left (live, per key)</span>
                <Button variant="secondary" size="sm" disabled={lsLimits.isFetching} onClick={() => lsLimits.refetch()}>
                  {lsLimits.isFetching ? "Checking…" : "Refresh"}
                </Button>
              </div>
              {lsLimits.isLoading ? (
                <Spinner />
              ) : !lsLimits.data?.keys.length ? (
                <p className="text-xs text-slate-400">No key configured yet.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {lsLimits.data.keys.map((k) => (
                    <div key={k.label} className="rounded-lg border border-slate-200 bg-white p-2.5">
                      <div className="mb-1.5 flex items-baseline justify-between">
                        <span className="text-xs font-semibold text-slate-800">{k.label}</span>
                        <span className="font-mono text-[11px] text-slate-400">{k.key}</span>
                      </div>
                      {!k.ok || !k.windows.length ? (
                        <p className="text-[11px] text-amber-700">{k.message || "no rate-limit info returned"}</p>
                      ) : (
                        k.windows.map((w) => {
                          const pct = w.limit ? Math.round((w.used / w.limit) * 100) : 0;
                          return (
                            <div key={w.window} className="mb-1.5">
                              <div className="flex items-baseline justify-between text-[11px]">
                                <span className="text-slate-500">per {w.window}</span>
                                <span className="tabular-nums text-slate-700">
                                  <span className={pct >= 80 ? "font-semibold text-amber-700" : ""}>{w.remaining.toLocaleString("nl-NL")}</span>
                                  <span className="text-slate-400"> / {w.limit.toLocaleString("nl-NL")} left</span>
                                </span>
                              </div>
                              <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                                <div className={`h-full ${pct >= 80 ? "bg-amber-500" : "bg-indigo-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                              </div>
                              <div className="text-[10px] text-slate-400">resets in {w.resetSec >= 60 ? `${Math.round(w.resetSec / 60)} min` : `${w.resetSec}s`}</div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-1 text-[11px] text-slate-400">
                Lightspeed meters per API key over three windows at once. Reading this costs one call itself, so it is not polled — hit Refresh when you want a fresh number.
              </p>
            </div>

            {lsTestResult && (
              <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${lsTestResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                {lsTestResult.results.map((r) => (
                  <div key={r.label}>
                    <span className="font-semibold">{r.label}:</span> {r.ok ? "✓" : "✗"} {r.message}
                  </div>
                ))}
              </div>
            )}

            <p className="mt-3 text-[11px] text-slate-400">
              Saving takes effect on the next API call — no restart needed. Each key is tested on its own credentials, so "Test connection" tells you exactly which one is wrong.
            </p>
          </Card>
        </div>
      )}

      {tab === "pbssrc" && isAdmin && (
        <div className="space-y-5">
          <Card
            title="PBS data source (FTP)"
            actions={
              <div className="flex items-center gap-2">
                {pbsSrc.data && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${pbsSrc.data.hasPassword ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${pbsSrc.data.hasPassword ? "bg-emerald-500" : "bg-amber-500"}`} /> {pbsSrc.data.hasPassword ? "password set" : "no password"}
                  </span>
                )}
                <Button variant="secondary" disabled={pbsTest.isPending} onClick={() => { setPbsTestResult(null); pbsTest.mutate(); }}>{pbsTest.isPending ? "Testing…" : "Test connection"}</Button>
              </div>
            }
          >
            <p className="mb-3 text-xs text-slate-500">
              The whole catalog (products, prices, stock, categories) is downloaded from the <span className="font-medium">PBS FTP feed</span>. Credentials are stored server-side and shown here (admin only) — use <span className="font-medium">Hide</span> to mask the password.
              Leave a field blank to fall back to the server's environment value.
            </p>
            <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:col-span-2">FTP host
                <Input value={pbs.host} onChange={(e) => setPbs({ ...pbs, host: e.target.value })} placeholder={pbsSrc.data?.envDefaults.host || "ftp.pbsbenelux.com"} />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">User
                <Input value={pbs.user} onChange={(e) => setPbs({ ...pbs, user: e.target.value })} placeholder={pbsSrc.data?.envDefaults.user || "username"} />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                <span className="flex items-center justify-between">Password
                  <button type="button" onClick={() => setShowPbsSecret((v) => !v)} className="normal-case text-[11px] font-normal text-indigo-600 hover:underline">{showPbsSecret ? "Hide" : "Show"}</button>
                </span>
                <Input type={showPbsSecret ? "text" : "password"} value={pbs.password} onChange={(e) => setPbs({ ...pbs, password: e.target.value })} placeholder="password" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Remote directory
                <Input value={pbs.dir} onChange={(e) => setPbs({ ...pbs, dir: e.target.value })} placeholder={pbsSrc.data?.envDefaults.dir || "Data"} />
                <span className="mt-0.5 text-[10px] normal-case text-slate-400">Folder holding price.xml / stock.xml / tree.xml / products.xml.</span>
              </label>
              <div className="flex items-end gap-4 text-sm text-slate-600">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={pbs.secure} onChange={(e) => setPbs({ ...pbs, secure: e.target.checked })} className="accent-indigo-600" /> Use FTPS (explicit TLS)</label>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button disabled={pbsSave.isPending || !pbs.host.trim() || !pbs.user.trim()} onClick={() => pbsSave.mutate({ host: pbs.host.trim(), user: pbs.user.trim(), password: pbs.password || undefined, dir: pbs.dir.trim(), secure: pbs.secure })}>
                {pbsSave.isPending ? "Saving…" : "Save PBS settings"}
              </Button>
              {pbsTestResult && (
                <span className={`inline-flex items-center gap-1.5 text-sm ${pbsTestResult.ok ? "text-emerald-700" : "text-red-600"}`}>
                  {pbsTestResult.ok ? "✓ Connected" : "✕ Failed"} — {pbsTestResult.message}
                </span>
              )}
            </div>
            {pbsTestResult?.files?.length ? (
              <div className="mt-4 overflow-x-auto rounded-lg border border-slate-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-medium">Feed file</th>
                      <th className="px-3 py-2 font-medium">Last modified</th>
                      <th className="px-3 py-2 font-medium">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pbsTestResult.files.map((f) => (
                      <tr key={f.name} className="border-b border-slate-50">
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-700">{f.name}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-600">{f.modifiedAt ? fmtDateTime(f.modifiedAt) : <span className="text-amber-600">missing</span>}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-600">{f.size != null ? `${(f.size / 1024 / 1024).toFixed(2)} MB` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Card>
        </div>
      )}

      {tab === "schedules" && isAdmin && (
        <div className="space-y-5">
          <Card title="Import schedules">
            <p className="mb-3 text-xs text-slate-500">
              Turn imports on/off and set when they run (<span className="font-medium">Europe/Amsterdam</span> time). Pick a time and the days — no technical knowledge needed. Schedules are stored here and executed by a
              single scheduled task on the server. Reconciles stay dry-run / approval-gated — scheduling one only builds the change set, it never auto-applies.
            </p>
            <div className="mb-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <span className="font-semibold">Server setup (once):</span> add one Plesk Scheduled Task that runs every minute:
              <code className="mt-1 block select-all rounded bg-white px-2 py-1 font-mono text-[11px] ring-1 ring-slate-200">/opt/plesk/node/25/bin/node httpdocs/dist/server/cli.js tick</code>
              It checks these schedules and runs whatever is due (each job is locked, so it never collides with a manual run). You can then remove the old per-job Plesk tasks.
            </div>
            {!schedRows ? <Spinner /> : (
              <table className="w-full max-w-3xl text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Import</th><th>On</th><th>When it runs</th><th>Next run</th><th>Last run</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {schedRows.map((s, i) => (
                    <tr key={s.id} className="align-middle">
                      <td className="py-2 font-medium text-slate-700">{s.label}<div className="font-mono text-[11px] text-slate-400">{s.id}</div></td>
                      <td><input type="checkbox" checked={s.enabled} onChange={(e) => setSchedRows(schedRows.map((r, j) => (j === i ? { ...r, enabled: e.target.checked } : r)))} className="accent-indigo-600" /></td>
                      <td>
                        {(() => {
                          const f = parseFriendlyCron(s.cron);
                          if (cronAdvanced || !f.ok) {
                            return <Input value={s.cron} onChange={(e) => setSchedCron(i, e.target.value)} className="w-44 font-mono text-xs" />;
                          }
                          return (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <input type="time" value={f.time} onChange={(e) => setSchedCron(i, buildCron(e.target.value || "08:00", f.days, f.dows))} className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                                <Select value={f.days} onChange={(e) => { const days = e.target.value as SchedDays; const dows = days === "weekdays" ? [1, 2, 3, 4, 5] : days === "daily" ? [0, 1, 2, 3, 4, 5, 6] : (f.dows.length ? f.dows : [1, 2, 3, 4, 5]); setSchedCron(i, buildCron(f.time, days, dows)); }} className="text-sm">
                                  <option value="weekdays">Weekdays (Mon–Fri)</option>
                                  <option value="daily">Every day</option>
                                  <option value="custom">Custom days…</option>
                                </Select>
                              </div>
                              {f.days === "custom" && (
                                <div className="flex flex-wrap gap-1">
                                  {DOW_PICKER.map(({ label, dow }) => { const on = f.dows.includes(dow); return (
                                    <button type="button" key={dow} onClick={() => { const set = new Set(f.dows); on ? set.delete(dow) : set.add(dow); setSchedCron(i, buildCron(f.time, "custom", [...set])); }} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${on ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
                                  ); })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-slate-500">{s.enabled ? fmtDateTime(s.nextRun) : <span className="text-slate-300">off</span>}</td>
                      <td className="text-slate-500">{s.lastRun ? fmtDateTime(s.lastRun) : <span className="text-slate-300">never</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button disabled={schedSave.isPending || !schedRows} onClick={() => schedRows && schedSave.mutate({ tasks: schedRows.map((r) => ({ id: r.id, cron: r.cron.trim(), enabled: r.enabled })) })}>{schedSave.isPending ? "Saving…" : "Save schedules"}</Button>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500"><input type="checkbox" checked={cronAdvanced} onChange={(e) => setCronAdvanced(e.target.checked)} className="accent-indigo-600" /> Advanced (edit cron directly)</label>
              <span className="text-[11px] text-slate-400">Pick a time and the days — saved in <span className="font-medium">NL time</span>. “Next run” updates after you save.{cronAdvanced ? <> Cron: <span className="font-mono">min hour day-of-month month day-of-week</span>, e.g. <span className="font-mono">0 8 * * 1-5</span> = 08:00 on weekdays.</> : null}</span>
            </div>
          </Card>
        </div>
      )}

      {tab === "users" && isAdmin && (
        <div className="space-y-5">
          <Card title="Users — who can sign in">
            <p className="mb-3 text-xs text-slate-500">
              Each person signs in with their own email + password. New users get a <span className="font-medium">temporary password</span> (shown
              once below) that they must change on first login. <span className="font-medium">Admins</span> can manage users and post live;
              <span className="font-medium"> users</span> can do everything else.
            </p>

            {tempCred && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm">
                <div>
                  Temporary password for <span className="font-medium">{tempCred.email}</span>:{" "}
                  <span className="select-all rounded bg-white px-2 py-0.5 font-mono font-semibold text-emerald-800 ring-1 ring-emerald-200">{tempCred.tempPassword}</span>
                  <div className="text-[11px] text-emerald-700">Share it securely — it's shown only once. They'll set their own on first login.</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTempCred(null)}>dismiss</Button>
              </div>
            )}

            {/* Add user */}
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Email
                <Input type="email" placeholder="name@bedrijf.nl" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="w-56" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Name (optional)
                <Input placeholder="Voornaam Achternaam" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="w-48" />
              </label>
              <label className="flex flex-col gap-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">Role
                <Select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as "user" | "admin" })}>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </Select>
              </label>
              <Button disabled={!newUser.email.trim() || userAdd.isPending} onClick={() => userAdd.mutate({ email: newUser.email.trim(), name: newUser.name.trim() || undefined, role: newUser.role })}>
                {userAdd.isPending ? "Adding…" : "+ Add user"}
              </Button>
            </div>

            {usersList.isLoading ? <Spinner /> : (
              <table className="w-full max-w-3xl text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Email</th><th>Name</th><th>Role</th><th>Status</th><th></th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(usersList.data ?? []).map((u) => (
                    <tr key={u.email} className="align-middle">
                      <td className="py-2 font-medium text-slate-700">{u.email}</td>
                      <td className="text-slate-500">{u.name ?? <span className="text-slate-300">—</span>}</td>
                      <td>
                        <Select value={u.role} onChange={(e) => userSetRole.mutate({ email: u.email, role: e.target.value as "user" | "admin" })} disabled={u.email === me.data?.email}>
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </Select>
                      </td>
                      <td className="text-xs">
                        {!u.hasPassword ? <Badge color="gray">no password</Badge> : u.mustChange ? <Badge color="amber">temp — must change</Badge> : <Badge color="green">active</Badge>}
                      </td>
                      <td className="space-x-2 whitespace-nowrap">
                        <button onClick={() => userReset.mutate({ email: u.email })} className="text-xs font-medium text-indigo-600 hover:underline">reset password</button>
                        {u.email !== me.data?.email && (
                          <button onClick={() => { if (confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) userRemove.mutate({ email: u.email }); }} className="text-xs font-medium text-red-600 hover:underline">remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {tab === "main-cat" && (
        <MainCatTab isAdmin={isAdmin} onFlash={(m) => showFlash(m)} onError={(e) => onErr(e as { message: string })} />
      )}

      {tab === "attr-map" && (
        <AttrMapTab isAdmin={isAdmin} onFlash={(m) => showFlash(m)} onError={(e) => onErr(e as { message: string })} />
      )}

      {tab === "eco-rules" && (
        <div className="space-y-6">
          {ecoStaleMappings.data && ecoStaleMappings.data.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-red-700">⚠ {ecoStaleMappings.data.length} stale eco path mapping{ecoStaleMappings.data.length === 1 ? "" : "s"} — LS category deleted in Lightspeed</p>
                <button
                  className="text-xs text-red-500 hover:underline"
                  onClick={() => {
                    if (confirm(`Remove all ${ecoStaleMappings.data.length} stale mappings?`)) {
                      ecoStaleMappings.data.forEach((e) => ecoDeleteEntry.mutate({ pbsCategoryId: e.pbsId, key: e.ecoKey as "ecologic" | "illogic" | "economic" | "economicEcologic" }));
                    }
                  }}
                >
                  Remove all
                </button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {ecoStaleMappings.data.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-red-800 bg-white/60 rounded px-2 py-0.5">
                    <span className="truncate mr-2"><span className="font-medium">{e.pbsPath}</span> — <span className="text-red-500">{e.ecoKey === "economicEcologic" ? "eco+economic" : e.ecoKey}</span>: "{e.lsTitle}" (id {e.lsId})</span>
                    <button onClick={() => ecoDeleteEntry.mutate({ pbsCategoryId: e.pbsId, key: e.ecoKey as "ecologic" | "illogic" | "economic" | "economicEcologic" })} className="shrink-0 text-red-400 hover:text-red-600">× remove</button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-red-500">These PBS categories will not receive their eco label during reconcile until remapped.</p>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">LS categories mapped via the app</p>
              <button onClick={() => setEcoTodayOpen((v) => !v)} className="text-xs text-indigo-600 hover:underline">
                {ecoTodayOpen ? "hide" : "show"}
              </button>
            </div>
            {ecoTodayOpen && (
              <div className="mt-2 mb-3 flex items-center gap-3">
                <label className="text-xs text-slate-600">Look back (hours)</label>
                <input type="number" min={1} max={168} value={ecoTodaySinceHours}
                  onChange={(e) => setEcoTodaySinceHours(Number(e.target.value))}
                  className="w-20 rounded border border-slate-300 px-2 py-1 text-xs"
                />
                <button onClick={() => ecoTodayCreations.refetch()} className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">Refresh</button>
              </div>
            )}
            {ecoTodayOpen && (
              ecoTodayCreations.isLoading ? <p className="mt-2 text-xs text-slate-400">Loading…</p> :
              !ecoTodayCreations.data?.entries.length ? (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-slate-400">No LS categories mapped in the last {ecoTodaySinceHours} hours.</p>
                  <button onClick={() => ecoDeleteOrphans.mutate({ sinceHours: 24, dryRun: true })} disabled={ecoDeleteOrphans.isPending} className="rounded bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50">
                    {ecoDeleteOrphans.isPending ? "Scanning…" : "Scan for orphaned LS categories"}
                  </button>
                  {ecoOrphanPreview && (
                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 space-y-1">
                      <p className="text-xs font-medium text-red-700">{ecoOrphanPreview.length} orphaned LS categor{ecoOrphanPreview.length === 1 ? "y" : "ies"} found — created today, no longer referenced in eco path mapping</p>
                      <div className="max-h-48 overflow-y-auto space-y-0.5">{ecoOrphanPreview.map(o => <div key={o.lsId} className="text-[11px] text-red-800">{o.lsPath} <span className="text-red-400">#{o.lsId}</span></div>)}</div>
                      <button onClick={() => { if (confirm(`Delete ${ecoOrphanPreview.length} orphaned LS categor${ecoOrphanPreview.length === 1 ? "y" : "ies"} from Lightspeed?`)) ecoDeleteOrphans.mutate({ sinceHours: 24, dryRun: false }); }} disabled={ecoDeleteOrphans.isPending} className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                        {ecoDeleteOrphans.isPending ? "Deleting…" : `Delete ${ecoOrphanPreview.length} orphans from Lightspeed`}
                      </button>
                    </div>
                  )}
                </div>
              ) :
              <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                <p className="text-xs text-slate-500">{ecoTodayCreations.data.totalLsIds} LS categories mapped today — {ecoTodayCreations.data.entries.length} unique LS categories</p>
                {ecoTodayCreations.data.entries.map((e) => (
                  <div key={e.lsId} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold text-slate-800">{e.lsTitle}</span>
                        <span className="ml-1 font-mono text-slate-400">#{e.lsId}</span>
                        {e.lsPath !== e.lsTitle && <div className="text-[11px] text-slate-400">{e.lsPath}</div>}
                      </div>
                      <Badge color={e.mappings.length > 1 ? "green" : "amber"}>{e.mappings.length} PBS</Badge>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {e.mappings.map((m, i) => (
                        <div key={i} className="text-slate-500">
                          <span className="text-[11px]">{m.pbsPath}</span>
                          <span className="ml-1 text-[10px] text-slate-400">({m.ecoKey === "economicEcologic" ? "eco+economic" : m.ecoKey})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="pt-1 border-t border-slate-100 flex items-center gap-3">
                  <button onClick={() => ecoDeleteOrphans.mutate({ sinceHours: 24, dryRun: true })} disabled={ecoDeleteOrphans.isPending} className="rounded bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50">
                    {ecoDeleteOrphans.isPending ? "Scanning…" : "Scan for orphaned LS categories"}
                  </button>
                  <span className="text-[11px] text-slate-400">Finds LS categories created today that are no longer referenced in any eco path mapping</span>
                </div>
                {ecoOrphanPreview && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 space-y-1">
                    <p className="text-xs font-medium text-red-700">{ecoOrphanPreview.length} orphaned LS categor{ecoOrphanPreview.length === 1 ? "y" : "ies"} — created today, no longer in eco path mapping</p>
                    <div className="max-h-48 overflow-y-auto space-y-0.5">{ecoOrphanPreview.map(o => <div key={o.lsId} className="text-[11px] text-red-800">{o.lsPath} <span className="text-red-400">#{o.lsId}</span></div>)}</div>
                    <button onClick={() => { if (confirm(`Delete ${ecoOrphanPreview.length} orphaned LS categor${ecoOrphanPreview.length === 1 ? "y" : "ies"} from Lightspeed?`)) ecoDeleteOrphans.mutate({ sinceHours: 24, dryRun: false }); }} disabled={ecoDeleteOrphans.isPending} className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                      {ecoDeleteOrphans.isPending ? "Deleting…" : `Delete ${ecoOrphanPreview.length} orphans from Lightspeed`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Revert incorrectly remapped panel */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800">Revert incorrectly remapped categories</p>
                <p className="text-[11px] text-orange-600 mt-0.5">Finds mappings that were remapped today but whose parent group has ≥ threshold products (should have been kept). Restores them to their original LS category.</p>
              </div>
              <button onClick={() => setEcoRevertOpen((v) => !v)} className="text-xs text-orange-700 hover:underline shrink-0 ml-4">
                {ecoRevertOpen ? "hide" : "show"}
              </button>
            </div>
            {ecoRevertOpen && (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-xs text-orange-700">Min. products (0 = revert everything)</label>
                  <input
                    type="number" min={0} max={500} value={ecoRevertThreshold}
                    onChange={(e) => setEcoRevertThreshold(Number(e.target.value))}
                    className="w-20 rounded border border-orange-300 px-2 py-1 text-xs"
                  />
                  <label className="text-xs text-orange-700 ml-2">Look back (hours)</label>
                  <input
                    type="number" min={1} max={168} value={ecoRevertSinceHours}
                    onChange={(e) => setEcoRevertSinceHours(Number(e.target.value))}
                    className="w-20 rounded border border-orange-300 px-2 py-1 text-xs"
                  />
                  <button onClick={() => ecoRevertAnalysis.refetch()} className="rounded bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700">
                    Refresh
                  </button>
                </div>
                {ecoRevertAnalysis.isLoading && <p className="text-xs text-orange-400">Scanning audit log…</p>}
                {ecoRevertAnalysis.data && (() => {
                  const { entries } = ecoRevertAnalysis.data;
                  if (entries.length === 0) return <p className="text-xs text-green-700 font-medium">No incorrectly remapped entries found — nothing to revert.</p>;
                  const revertPayload = entries.filter(e => e.originalStillExists).map(e => ({
                    pbsCategoryId: e.pbsId,
                    ecoKey: e.ecoKey as "ecologic" | "illogic" | "economic" | "economicEcologic",
                    originalLsId: e.originalLsId,
                    originalLsTitle: e.originalLsTitle,
                  }));
                  const missingCount = entries.filter(e => !e.originalStillExists).length;
                  return (
                    <>
                      <p className="text-xs text-orange-700 font-medium">{entries.length} incorrectly remapped entr{entries.length === 1 ? "y" : "ies"} found (group count ≥ {ecoRevertThreshold})</p>
                      {missingCount > 0 && <p className="text-xs text-red-600">⚠ {missingCount} original LS categor{missingCount === 1 ? "y has" : "ies have"} been deleted — cannot revert those.</p>}
                      <div className="max-h-72 overflow-y-auto space-y-1">
                        {entries.map((e) => (
                          <div key={`${e.pbsId}.${e.ecoKey}`} className={`rounded border px-3 py-2 text-xs ${e.originalStillExists ? "border-orange-200 bg-white" : "border-red-200 bg-red-50"}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-800">{e.pbsPath}</span>
                              <span className="text-[11px] text-slate-400 shrink-0">{e.groupCount} products in group · {e.ecoKey === "economicEcologic" ? "eco+economic" : e.ecoKey}</span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              <span className="line-through opacity-60">{e.currentLsTitle}</span>
                              {" → "}
                              {e.originalStillExists
                                ? <span className="text-green-700 font-medium">{e.originalLsTitle}</span>
                                : <span className="text-red-600">{e.originalLsTitle} <span className="font-normal">(deleted — cannot revert)</span></span>
                              }
                            </div>
                            {e.originalLsPath !== e.originalLsTitle && <div className="text-[10px] text-slate-400">{e.originalLsPath}</div>}
                          </div>
                        ))}
                      </div>
                      {revertPayload.length > 0 && (
                        <button
                          onClick={() => { if (confirm(`Revert ${revertPayload.length} mapping${revertPayload.length === 1 ? "" : "s"} back to their original LS category?`)) ecoRevertMutation.mutate({ entries: revertPayload }); }}
                          disabled={ecoRevertMutation.isPending}
                          className="rounded bg-orange-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                        >
                          {ecoRevertMutation.isPending ? "Reverting…" : `Revert ${revertPayload.length} mapping${revertPayload.length === 1 ? "" : "s"} to original`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          {/* Threshold cleanup panel */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">Threshold cleanup — remap leaf categories with few products</p>
              <button onClick={() => setEcoCleanupOpen((v) => !v)} className="text-xs text-indigo-600 hover:underline">
                {ecoCleanupOpen ? "hide" : "show"}
              </button>
            </div>
            {ecoCleanupOpen && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-slate-500">PBS categories with fewer than the threshold number of (active) products are remapped to their parent LS category. The now-unused leaf LS category is then deleted from Lightspeed.</p>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-slate-600">Min. products (threshold)</label>
                  <input
                    type="number" min={1} max={500} value={ecoCleanupThreshold}
                    onChange={(e) => { setEcoCleanupThreshold(Number(e.target.value)); setEcoCleanupEnabled(false); setEcoCleanupForceKeep(new Set()); setEcoCleanupForceRemap(new Set()); }}
                    className="w-20 rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <button
                    onClick={() => { setEcoCleanupEnabled(true); setEcoCleanupForceKeep(new Set()); setEcoCleanupForceRemap(new Set()); }}
                    className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                  >
                    Analyse
                  </button>
                </div>
                {ecoThresholdAnalysis.isLoading && <p className="text-xs text-slate-400">Analysing…</p>}
                {ecoThresholdAnalysis.data && (() => {
                  const { entries } = ecoThresholdAnalysis.data;
                  // forceRemap wins over forceKeep wins over algorithm
                  const effectiveKeep = (e: typeof entries[number]) => {
                    if (ecoCleanupForceRemap.has(e.pbsId)) return false;
                    if (ecoCleanupForceKeep.has(e.pbsId)) return true;
                    return e.keep;
                  };
                  const toggleGroupKeep = (parentPbsPath: string | null, nowForceKeep: boolean) => {
                    const ids = entries.filter(e => e.parentPbsPath === parentPbsPath).map(e => e.pbsId);
                    if (nowForceKeep) {
                      setEcoCleanupForceKeep(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
                      setEcoCleanupForceRemap(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
                    } else {
                      setEcoCleanupForceRemap(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
                      setEcoCleanupForceKeep(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
                    }
                  };
                  const remapEntries = entries
                    .filter((e) => !effectiveKeep(e))
                    .flatMap((e) =>
                      e.trees
                        .filter((t) => t.parentLsId !== null)
                        .map((t) => ({
                          pbsCategoryId: e.pbsId,
                          ecoKey: t.ecoKey as "ecologic" | "illogic" | "economic" | "economicEcologic",
                          currentLsId: t.lsId,
                          parentLsId: t.parentLsId!,
                          parentLsTitle: t.parentLsTitle!,
                        }))
                    );
                  const keepCount = entries.filter(e => effectiveKeep(e)).length;
                  const remapCount = entries.filter(e => !effectiveKeep(e)).length;
                  const noParentCount = entries.filter((e) => !effectiveKeep(e) && e.trees.some((t) => t.parentLsId === null)).length;
                  // Group entries by parentPbsPath for the group-level keep button
                  const groupedByParent = new Map<string, typeof entries>();
                  for (const e of entries) {
                    const key = e.parentPbsPath ?? "__root__";
                    if (!groupedByParent.has(key)) groupedByParent.set(key, []);
                    groupedByParent.get(key)!.push(e);
                  }
                  return (
                    <>
                      <p className="text-[11px] text-slate-400">Threshold checked against <strong>parent PBS group total</strong>. Use the <strong>Keep group</strong> button to manually override groups the algorithm misclassifies.</p>
                      <div className="flex gap-4 text-xs">
                        <span className="text-green-700 font-medium">✓ {keepCount} keep</span>
                        <span className="text-amber-700 font-medium">↑ {remapCount} remap to parent</span>
                        {(ecoCleanupForceKeep.size > 0 || ecoCleanupForceRemap.size > 0) && <span className="text-indigo-600 font-medium">{ecoCleanupForceKeep.size + ecoCleanupForceRemap.size} manual overrides</span>}
                        {noParentCount > 0 && <span className="text-red-600 font-medium">⚠ {noParentCount} no parent found</span>}
                      </div>
                      {entries.length > 0 && (
                        <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
                          {[...groupedByParent.entries()].map(([groupKey, groupEntries]) => {
                            if (!groupEntries.length) return null;
                            const allKept = groupEntries.every(e => effectiveKeep(e));
                            const parentPath = groupEntries[0]!.parentPbsPath;
                            // Group total = groupCount of first entry (all siblings share the same parent count)
                            const groupTotal = groupEntries[0]!.groupCount;
                            const siblingCount = groupEntries.length;
                            return (
                              <div key={groupKey} className="rounded border border-slate-200 bg-white overflow-hidden">
                                {/* Group header — shows the TOTAL for all siblings combined (not per row) */}
                                <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-1.5 border-b border-slate-100">
                                  <span className="text-[11px] font-semibold text-slate-600">
                                    {parentPath ?? "Root"}
                                    <span className="ml-2 font-normal text-slate-400">{siblingCount} sub-categories · {groupTotal} active products total</span>
                                  </span>
                                  <button
                                    onClick={() => toggleGroupKeep(parentPath, !allKept)}
                                    className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium ${allKept ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                                  >
                                    {allKept ? "Remap group" : "Keep group"}
                                  </button>
                                </div>
                                {/* Entries in group */}
                                <div className="divide-y divide-slate-50">
                                  {groupEntries.map((e) => {
                                    const kept = effectiveKeep(e);
                                    return (
                                      <div key={e.pbsId} className={`px-3 py-2 text-xs ${kept ? "bg-green-50" : "bg-amber-50"}`}>
                                        <div className="flex items-center justify-between gap-2">
                                          <span className={`font-medium ${kept ? "text-green-800" : "text-amber-800"}`}>
                                            {e.pbsPath.split(" › ").slice(-1)[0]}
                                            {ecoCleanupForceKeep.has(e.pbsId) && <span className="ml-1 text-[10px] text-indigo-500">(manual keep)</span>}
                                            {ecoCleanupForceRemap.has(e.pbsId) && <span className="ml-1 text-[10px] text-indigo-500">(manual remap)</span>}
                                          </span>
                                          <span className="flex items-center gap-1.5 shrink-0">
                                            <span className="text-[11px] text-slate-400">{e.productCount} products</span>
                                            <button
                                              onClick={() => {
                                                if (kept) {
                                                  setEcoCleanupForceRemap(prev => { const n = new Set(prev); n.add(e.pbsId); return n; });
                                                  setEcoCleanupForceKeep(prev => { const n = new Set(prev); n.delete(e.pbsId); return n; });
                                                } else {
                                                  setEcoCleanupForceKeep(prev => { const n = new Set(prev); n.add(e.pbsId); return n; });
                                                  setEcoCleanupForceRemap(prev => { const n = new Set(prev); n.delete(e.pbsId); return n; });
                                                }
                                              }}
                                              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${kept ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                                            >
                                              {kept ? "Remap" : "Keep"}
                                            </button>
                                          </span>
                                        </div>
                                        {!kept && (
                                          <div className="mt-0.5 space-y-0.5">
                                            {e.trees.map((t) => (
                                              <div key={t.ecoKey} className={`text-[11px] ${t.parentLsId ? "text-slate-500" : "text-red-500"}`}>
                                                <span className="font-medium">{t.ecoKey === "economicEcologic" ? "eco+economic" : t.ecoKey}</span>
                                                {": "}
                                                <span className="line-through opacity-60">{t.lsTitle}</span>
                                                {" → "}
                                                {t.parentLsTitle ?? <span className="text-red-500">no parent</span>}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {remapEntries.length > 0 && (
                        <button
                          onClick={() => {
                            if (confirm(`Remap ${remapEntries.length} mapping${remapEntries.length === 1 ? "" : "s"} to parent and delete ${new Set(remapEntries.map((e) => e.currentLsId)).size} leaf LS categor${new Set(remapEntries.map((e) => e.currentLsId)).size === 1 ? "y" : "ies"}?`))
                              ecoCleanupMutation.mutate({ entries: remapEntries });
                          }}
                          disabled={ecoCleanupMutation.isPending}
                          className="rounded bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                          {ecoCleanupMutation.isPending ? "Cleaning up…" : `Apply cleanup — remap ${remapEntries.length} × parent + delete ${new Set(remapEntries.map((e) => e.currentLsId)).size} leaf LS categories`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <Card title="Eco label category mapping">
            <p className="mb-3 text-xs text-slate-500">
              Map each of the four eco rule labels to the corresponding Lightspeed category. These categories are auto-assigned to products during reconciliation based on the Ecologic flag and brand Economic status.
            </p>
            <table className="w-full max-w-2xl text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="py-2 w-40">Label</th><th>Assigned when</th><th>Lightspeed category</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(["ecologic", "illogic", "economic", "economicEcologic"] as const).map((key) => {
                  const labels: Record<string, { rule: string; color: string }> = {
                    ecologic: { rule: "Ecologic = yes, brand = any", color: "bg-green-100 text-green-800" },
                    illogic: { rule: "Ecologic = no, brand = any", color: "bg-amber-100 text-amber-800" },
                    economic: { rule: "Ecologic = no, brand = economic", color: "bg-blue-100 text-blue-800" },
                    economicEcologic: { rule: "Ecologic = yes, brand = economic", color: "bg-teal-100 text-teal-800" },
                  };
                  const ref = ecoLabels.data?.[key];
                  const info = labels[key];
                  return (
                    <tr key={key}>
                      <td className="py-2"><Badge color={key === "ecologic" ? "green" : key === "illogic" ? "amber" : key === "economic" ? "blue" : "gray"}>{key === "economicEcologic" ? "economic/ecologic" : key}</Badge></td>
                      <td className="text-xs text-slate-500">{info?.rule}</td>
                      <td>
                        {ref
                          ? <span className="font-medium text-slate-700">{ref.lsTitle} <span className="text-slate-400 font-mono text-xs">#{ref.lsId}</span></span>
                          : <span className="text-slate-400 text-xs italic">not configured</span>
                        }
                      </td>
                      <td className="space-x-2 whitespace-nowrap">
                        <button className="text-xs font-medium text-indigo-600 hover:underline" onClick={() => setEcoLabelPicker(key)}>
                          {ref ? "change" : "set"}
                        </button>
                        {ref && <button className="text-xs font-medium text-red-600 hover:underline" onClick={() => { if (confirm(`Remove the "${key === "economicEcologic" ? "economic/ecologic" : key}" eco label mapping?`)) ecoLabelDelete.mutate({ key }); }}>remove</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {ecoLabelPicker && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-slate-600">Pick LS category for <strong>{ecoLabelPicker === "economicEcologic" ? "economic/ecologic" : ecoLabelPicker}</strong>:</span>
                <LsPicker kind="category" onPick={(lsId, lsTitle) => { ecoLabelSet.mutate({ key: ecoLabelPicker, lsId, lsTitle }); setEcoLabelPicker(null); }} placeholder="search categories…" />
                <Button variant="secondary" size="sm" onClick={() => setEcoLabelPicker(null)}>cancel</Button>
              </div>
            )}
          </Card>

          <Card title="Eco path auto-map">
            <p className="mb-3 text-xs text-slate-500">
              For each PBS category with a primary mapping, finds the matching subcategory in each eco label tree. Preview the suggestions, select rows to approve individually (shift-click for ranges), then apply. Only adds new entries — never overwrites existing ones.
            </p>
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 flex items-center justify-between gap-4">
              <p className="text-xs text-red-700">Reset: wipes the entire eco path mapping so you can start over. The LS categories in Lightspeed are NOT deleted — only the mappings in this app.</p>
              <button
                onClick={() => { if (confirm("Wipe ALL eco path mappings? This cannot be undone. LS categories in Lightspeed are NOT deleted.")) ecoClearAllMappings.mutate(); }}
                disabled={ecoClearAllMappings.isPending}
                className="shrink-0 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {ecoClearAllMappings.isPending ? "Clearing…" : "Clear all mappings"}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" disabled={ecoAutoSuggest.isPending} onClick={() => { setEcoAutoPreview(null); setSelEcoRows(new Set()); ecoAutoSuggest.mutate(); }}>
                {ecoAutoSuggest.isPending ? "Scanning…" : "⚡ Preview auto-map"}
              </Button>
              {ecoAutoPreview && (
                <>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600">
                    Sort
                    <Select value={ecoAutoSort} onChange={(e) => setEcoAutoSort(e.target.value as typeof ecoAutoSort)} className="py-1">
                      <option value="coverage-asc">Coverage ↑ (worst first)</option>
                      <option value="coverage-desc">Coverage ↓ (best first)</option>
                      <option value="pbs">PBS path A→Z</option>
                    </Select>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600">
                    Depth
                    <Select value={ecoDepthFilter ?? ""} onChange={(e) => setEcoDepthFilter(e.target.value === "" ? null : Number(e.target.value))} className="py-1">
                      <option value="">Any</option>
                      {[1,2,3,4,5,6].map((d) => <option key={d} value={d}>{d} level{d !== 1 ? "s" : ""}</option>)}
                    </Select>
                  </label>
                  <button
                    onClick={() => setEcoAutoCompact((v) => !v)}
                    className={`rounded border px-2.5 py-1 text-xs font-medium transition ${ecoAutoCompact ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                  >
                    {ecoAutoCompact ? "Compact ✓" : "Compact"}
                  </button>
                  <Button
                    disabled={ecoAutoApply.isPending}
                    onClick={() => {
                      const ids = selEcoRows.size > 0 ? [...selEcoRows] : null;
                      const count = ids ? ids.length : ecoAutoPreview.totalMatched;
                      const msg = ids
                        ? `Apply suggestions for ${count} selected row${count === 1 ? "" : "s"}? Existing entries will be updated to the shown suggestion.`
                        : `Apply all ${count} suggestions? Only empty cells are filled — existing entries are not changed.`;
                      if (confirm(msg)) {
                        ecoAutoApply.mutate(ids ? { pbsCategoryIds: ids, overwrite: true } : {});
                      }
                    }}
                  >
                    {ecoAutoApply.isPending ? "Applying…" : selEcoRows.size > 0 ? `Apply ${selEcoRows.size} selected` : `Apply all ${ecoAutoPreview.totalMatched}`}
                  </Button>
                  <button onClick={() => { setEcoAutoPreview(null); setSelEcoRows(new Set()); }} className="text-xs text-slate-400 hover:text-slate-600">dismiss</button>
                </>
              )}
            </div>

            {ecoAutoPreview && (() => {
              const depthOf = (path: string) => path ? path.split("›").length : 1;
              const sorted = [...ecoAutoPreview.suggestions]
                .filter((s) => ecoDepthFilter === null || depthOf(s.pbsPath) === ecoDepthFilter)
                .sort((a, b) =>
                  ecoAutoSort === "coverage-asc" ? a.matchCount - b.matchCount || a.pbsPath.localeCompare(b.pbsPath) :
                  ecoAutoSort === "coverage-desc" ? b.matchCount - a.matchCount || a.pbsPath.localeCompare(b.pbsPath) :
                  a.pbsPath.localeCompare(b.pbsPath)
                );
              const sortedIds = sorted.map((s) => s.pbsId);
              const allSelected = sortedIds.length > 0 && sortedIds.every((id) => selEcoRows.has(id));
              const coverageColor = (n: number) => n === 4 ? "text-emerald-700 bg-emerald-50" : n >= 2 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
              return (
                <div className="mt-4">
                  <p className="mb-2 text-xs text-slate-600">
                    Found <span className="font-semibold">{ecoAutoPreview.totalMatched}</span> matchable PBS categories (of {ecoAutoPreview.totalPbs} with a primary mapping){ecoDepthFilter !== null ? <> — <span className="font-semibold">{sorted.length}</span> shown at depth {ecoDepthFilter}</> : ""}.
                    {ecoAutoPreview.unconfiguredRoots.length > 0 && <span className="ml-1 text-amber-700">Label roots not configured: {ecoAutoPreview.unconfiguredRoots.join(", ")} — configure them in the card above first.</span>}
                    {selEcoRows.size > 0 && <span className="ml-2 font-medium text-indigo-700">{selEcoRows.size} selected.</span>}
                    {selEcoRows.size > 0 && <button className="ml-1 text-xs text-indigo-500 hover:underline" onClick={() => setSelEcoRows(new Set())}>clear</button>}
                  </p>
                  <div className="overflow-x-auto" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                    <table className={`w-full text-left ${ecoAutoCompact ? "text-xs [&>tbody>tr>td]:py-0.5" : "text-sm"}`}>
                      <thead className="sticky top-0 z-10 bg-white text-[11px] uppercase tracking-wide text-slate-400 shadow-[0_1px_0_0_theme(colors.slate.200)]">
                        <tr>
                          <th className="w-8 py-1.5">
                            <input type="checkbox" checked={allSelected} onChange={() => { const n = new Set(allSelected ? [] : sortedIds); setSelEcoRows(n); ecoRangeAnchor.current = null; }} className="accent-indigo-600" />
                          </th>
                          <th className="py-1.5 pr-3 min-w-[180px]">PBS category</th>
                          <th className="py-1.5 pr-3 min-w-[160px]"><Badge color="green">ecologic</Badge></th>
                          <th className="py-1.5 pr-3 min-w-[160px]"><Badge color="amber">illogic</Badge></th>
                          <th className="py-1.5 pr-3 min-w-[160px]"><Badge color="blue">economic</Badge></th>
                          <th className="py-1.5 pr-3 min-w-[160px]"><Badge color="gray">eco+economic</Badge></th>
                          <th className="py-1.5 text-center w-12">match</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sorted.map((s) => (
                          <tr key={s.pbsId} className={`align-top ${selEcoRows.has(s.pbsId) ? "bg-indigo-50/50" : ""}`}>
                            <td className="py-1.5 pr-2">
                              <input
                                type="checkbox"
                                checked={selEcoRows.has(s.pbsId)}
                                className="accent-indigo-600"
                                onClick={(e) => selectRow(sortedIds, s.pbsId, e.shiftKey, selEcoRows, setSelEcoRows, ecoRangeAnchor)}
                                onChange={() => {}}
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <div className="font-medium text-slate-700 leading-snug">{s.pbsName}</div>
                              {s.pbsPath !== s.pbsName && <div className="text-[11px] text-slate-400 leading-tight">{s.pbsPath}</div>}
                            </td>
                            {(["ecologic", "illogic", "economic", "economicEcologic"] as const).map((k) => {
                              const cellKey = `${s.pbsId}:${k}`;
                              const isEditing = ecoEditCell === cellKey;
                              const isSavingUpsert = ecoUpsertEntry.isPending && ecoUpsertEntry.variables?.pbsCategoryId === s.pbsId && ecoUpsertEntry.variables?.key === k;
                              const isSavingCreate = (ecoCreateLsCategory.isPending && ecoCreateLsCategory.variables?.pbsCategoryId === s.pbsId && ecoCreateLsCategory.variables?.ecoKey === k) || (ecoCreateLsCategoryForTrees.isPending && ecoCreateLsCategoryForTrees.variables?.pbsCategoryId === s.pbsId);
                              const newCatName = ecoEditQuery.trim() || s.pbsName;
                              const otherEmptyKeys = (["ecologic", "illogic", "economic", "economicEcologic"] as const).filter((ok) => ok !== k);
                              return (
                              <td key={k} className="py-1.5 pr-3">
                                {isEditing ? (
                                  <div className="space-y-1.5">
                                    {(isSavingUpsert || isSavingCreate) ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600"><span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500" />Saving…</span>
                                    ) : (
                                      <>
                                        <LsPicker kind="category" query={ecoEditQuery} onQueryChange={setEcoEditQuery} onPick={(id, t) => ecoUpsertEntry.mutate({ pbsCategoryId: s.pbsId, key: k, lsId: id, lsTitle: t })} placeholder="search LS category…" pathPrefix={ecoLabels.data?.[k]?.lsTitle} />
                                        <div className="flex gap-2">
                                          <button onClick={() => setEcoEditCreating((v) => !v)} className="text-[11px] text-indigo-600 hover:underline">+ create in LS</button>
                                          <button onClick={closeEcoEditor} className="text-[11px] text-slate-400 hover:underline">cancel</button>
                                        </div>
                                        {ecoEditCreating && (() => {
                                          const allSegments = [newCatName, ...ecoCreateSubSegments.filter(Boolean)];
                                          const pathPreview = [ecoEditParent?.lsTitle, ...allSegments].filter(Boolean).join(" › ");
                                          const additionalKeys = (Object.keys(ecoCreateAdditional) as ("ecologic"|"illogic"|"economic"|"economicEcologic")[]);
                                          const allTrees = [{ ecoKey: k, parentLsId: ecoEditParent?.lsId }, ...additionalKeys.map((ok) => ({ ecoKey: ok, parentLsId: ecoCreateAdditional[ok]?.lsId }))];
                                          const treeNames = allTrees.map((t) => t.ecoKey === "economicEcologic" ? "eco+economic" : t.ecoKey).join(", ");
                                          const hasSubSegments = ecoCreateSubSegments.some(Boolean);
                                          return (
                                          <div className="rounded border border-indigo-200 bg-indigo-50/50 px-2 py-1.5 space-y-2">
                                            <div className="text-[11px] text-slate-500">Path: <span className="font-semibold text-slate-700">{pathPreview || "…"}</span></div>
                                            <div className="space-y-1">
                                              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{k === "economicEcologic" ? "eco+economic" : k} — parent of "{newCatName}":</div>
                                              <LsPicker kind="category" onPick={(id, t) => setEcoEditParent({ lsId: id, lsTitle: t })} placeholder="pick parent…" pathPrefix={ecoLabels.data?.[k]?.lsTitle} />
                                              {ecoEditParent && <div className="text-[11px]"><Badge color="blue">{ecoEditParent.lsTitle}</Badge> <button onClick={() => setEcoEditParent(null)} className="text-slate-400 hover:text-red-500">×</button></div>}
                                            </div>
                                            {ecoCreateSubSegments.map((seg, i) => (
                                              <div key={i} className="space-y-0.5">
                                                <div className="text-[11px] font-medium text-slate-500">Sub-level {i + 1} name:</div>
                                                <div className="flex gap-1">
                                                  <input
                                                    className="flex-1 rounded border border-slate-200 px-2 py-0.5 text-xs focus:border-indigo-400 focus:outline-none"
                                                    placeholder={`sub-category name…`}
                                                    value={seg}
                                                    onChange={(e) => setEcoCreateSubSegments((prev) => prev.map((v, j) => j === i ? e.target.value : v))}
                                                  />
                                                  <button onClick={() => setEcoCreateSubSegments((prev) => prev.filter((_, j) => j !== i))} className="text-[11px] text-slate-400 hover:text-red-500 px-1">×</button>
                                                </div>
                                              </div>
                                            ))}
                                            <button onClick={() => setEcoCreateSubSegments((prev) => [...prev, ""])} className="text-[11px] text-indigo-600 hover:underline">+ Add sub-level ›</button>
                                            {otherEmptyKeys.length > 0 && (
                                              <div className="space-y-1.5 border-t border-indigo-100 pt-1.5">
                                                <div className="text-[11px] text-slate-500">Also create in:</div>
                                                {otherEmptyKeys.map((ok) => {
                                                  const isChecked = ok in ecoCreateAdditional;
                                                  const addParent = ecoCreateAdditional[ok] ?? null;
                                                  return (
                                                    <div key={ok} className="space-y-1">
                                                      <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 cursor-pointer">
                                                        <input type="checkbox" className="accent-indigo-600" checked={isChecked} onChange={(e) => { const n = { ...ecoCreateAdditional }; if (e.target.checked) n[ok] = null; else delete n[ok]; setEcoCreateAdditional(n); }} />
                                                        {ok === "economicEcologic" ? "eco+economic" : ok}
                                                      </label>
                                                      {isChecked && (
                                                        <div className="pl-4 space-y-0.5">
                                                          <LsPicker kind="category" onPick={(id, t) => setEcoCreateAdditional((prev) => ({ ...prev, [ok]: { lsId: id, lsTitle: t } }))} placeholder={`parent of "${newCatName}"…`} pathPrefix={ecoLabels.data?.[ok]?.lsTitle} />
                                                          {addParent && <div className="text-[11px]"><Badge color="blue">{addParent.lsTitle}</Badge> <button onClick={() => setEcoCreateAdditional((prev) => ({ ...prev, [ok]: null }))} className="text-slate-400 hover:text-red-500">×</button></div>}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                            <Button size="sm" onClick={() => {
                                              if (confirm(`Create path "${pathPreview}" in ${allTrees.length} tree${allTrees.length === 1 ? "" : "s"} (${treeNames}) in Lightspeed?`)) {
                                                if (hasSubSegments) {
                                                  if (allTrees.length === 1) {
                                                    ecoCreateLsCategoryPath.mutate({ pbsCategoryId: s.pbsId, ecoKey: k, segments: allSegments, parentLsId: ecoEditParent?.lsId });
                                                  } else {
                                                    ecoCreateLsCategoryPathForTrees.mutate({ pbsCategoryId: s.pbsId, segments: allSegments, trees: allTrees });
                                                  }
                                                } else {
                                                  if (allTrees.length === 1) {
                                                    ecoCreateLsCategory.mutate({ pbsCategoryId: s.pbsId, ecoKey: k, title: newCatName, parentLsId: ecoEditParent?.lsId });
                                                  } else {
                                                    ecoCreateLsCategoryForTrees.mutate({ pbsCategoryId: s.pbsId, title: newCatName, trees: allTrees });
                                                  }
                                                }
                                              }
                                            }}>
                                              Create &amp; map
                                            </Button>
                                          </div>
                                          );
                                        })()}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="group flex items-start gap-1">
                                    {s[k] ? (
                                      <div>
                                        <div className="font-medium text-emerald-700 leading-snug">{s[k]!.lsTitle}</div>
                                        {s[k]!.lsPath !== s[k]!.lsTitle && <div className="text-[11px] text-slate-400 leading-tight">{s[k]!.lsPath}</div>}
                                      </div>
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                    <button onClick={() => { setEcoEditCell(cellKey); setEcoEditCreating(false); setEcoEditParent(null); setEcoEditQuery(""); }} className="invisible group-hover:visible shrink-0 mt-0.5 text-[11px] text-slate-400 hover:text-indigo-600" title="Override or create new LS category">✎</button>
                                  </div>
                                )}
                              </td>
                              );
                            })}
                            <td className="py-1.5 text-center">
                              <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${coverageColor(s.matchCount)}`}>{s.matchCount}/4</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white mt-0">
                    <div className="flex items-center gap-3 px-2 py-2">
                      {selEcoRows.size > 0 && <span className="text-xs text-indigo-700 font-medium">{selEcoRows.size} selected</span>}
                      <Button
                        disabled={ecoAutoApply.isPending}
                        onClick={() => {
                          const ids = selEcoRows.size > 0 ? [...selEcoRows] : null;
                          const count = ids ? ids.length : ecoAutoPreview.totalMatched;
                          const msg = ids
                            ? `Apply suggestions for ${count} selected row${count === 1 ? "" : "s"}? Existing entries will be updated to the shown suggestion.`
                            : `Apply all ${count} suggestions? Only empty cells are filled — existing entries are not changed.`;
                          if (confirm(msg)) {
                            ecoAutoApply.mutate(ids ? { pbsCategoryIds: ids, overwrite: true } : {});
                          }
                        }}
                      >
                        {ecoAutoApply.isPending ? "Applying…" : selEcoRows.size > 0 ? `Apply ${selEcoRows.size} selected` : `Apply all ${ecoAutoPreview.totalMatched}`}
                      </Button>
                      {selEcoRows.size > 0 && (
                        <button
                          className={`rounded border px-2.5 py-1 text-xs font-medium transition ${ecoBulkOpen ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                          onClick={() => { setEcoBulkOpen((v) => !v); setEcoBulkKeys(new Set()); setEcoBulkPicks({}); setEcoBulkQueries({}); }}
                        >
                          Set column…
                        </button>
                      )}
                      <button onClick={() => { setEcoAutoPreview(null); setSelEcoRows(new Set()); setEcoBulkOpen(false); setEcoBulkKeys(new Set()); setEcoBulkPicks({}); }} className="text-xs text-slate-400 hover:text-slate-600 ml-auto">dismiss</button>
                    </div>
                    {ecoBulkOpen && selEcoRows.size > 0 && (
                      <div className="border-t border-slate-100 px-3 pb-3 pt-2 flex flex-col gap-3">
                        <p className="text-xs text-slate-500">Select one or more eco tree columns to set for <span className="font-semibold text-indigo-700">{selEcoRows.size} selected row{selEcoRows.size === 1 ? "" : "s"}</span>:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(["ecologic", "illogic", "economic", "economicEcologic"] as const).map((k) => {
                            const active = ecoBulkKeys.has(k);
                            return (
                              <label key={k} className={`flex items-center gap-1.5 cursor-pointer rounded border px-2 py-0.5 text-xs font-medium transition ${active ? "border-violet-400 bg-violet-100 text-violet-800" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                                <input type="checkbox" checked={active} className="accent-violet-600" onChange={() => {
                                  setEcoBulkKeys((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
                                  setEcoBulkPicks((prev) => { const n = { ...prev }; delete n[k]; return n; });
                                  setEcoBulkQueries((prev) => { const n = { ...prev }; delete n[k]; return n; });
                                }} />
                                {k === "economicEcologic" ? "eco+economic" : k}
                              </label>
                            );
                          })}
                        </div>
                        {[...ecoBulkKeys].map((k) => (
                          <div key={k} className="flex items-center gap-2">
                            <span className="w-28 shrink-0 text-xs font-medium text-violet-700">{k === "economicEcologic" ? "eco+economic" : k}:</span>
                            <div className="flex-1">
                              <LsPicker
                                kind="category"
                                query={ecoBulkQueries[k] ?? ""}
                                onQueryChange={(v) => setEcoBulkQueries((prev) => ({ ...prev, [k]: v }))}
                                placeholder={`search in ${k === "economicEcologic" ? "eco+economic" : k}…`}
                                pathPrefix={ecoLabels.data?.[k]?.lsTitle}
                                onPick={(id, t, p) => { setEcoBulkPicks((prev) => ({ ...prev, [k]: { lsId: id, lsTitle: t, lsPath: p } })); setEcoBulkQueries((prev) => ({ ...prev, [k]: t })); }}
                              />
                            </div>
                            {ecoBulkPicks[k] && <span className="text-xs text-slate-400 truncate max-w-[140px]">✓ {ecoBulkPicks[k]!.lsTitle}</span>}
                          </div>
                        ))}
                        {ecoBulkKeys.size > 0 && [...ecoBulkKeys].every((k) => ecoBulkPicks[k]) && (
                          <div>
                            <Button
                              disabled={ecoBulkUpsertMulti.isPending}
                              onClick={() => {
                                const trees = [...ecoBulkKeys].map((k) => ({ key: k, lsId: ecoBulkPicks[k]!.lsId, lsTitle: ecoBulkPicks[k]!.lsTitle, lsPath: ecoBulkPicks[k]!.lsPath }));
                                const colNames = trees.map((t) => `${t.key === "economicEcologic" ? "eco+economic" : t.key}: "${t.lsTitle}"`).join(", ");
                                if (confirm(`Set ${colNames} for ${selEcoRows.size} selected row${selEcoRows.size === 1 ? "" : "s"}?`)) {
                                  ecoBulkUpsertMulti.mutate({ pbsCategoryIds: [...selEcoRows], trees });
                                }
                              }}
                            >
                              {ecoBulkUpsertMulti.isPending ? "Saving…" : `Set for ${selEcoRows.size} rows`}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </Card>

          <Card title="Rule results">
            <p className="mb-3 text-xs text-slate-500">
              Shows the auto-category each product receives based on its Ecologic flag and its brand's Economic setting. Brand not in the map = only base label assigned (no Economic category).
            </p>
            <div className="mb-3 flex items-center gap-3">
              <Input
                value={ecoBrandFilter}
                onChange={(e) => {
                  setEcoBrandFilter(e.target.value);
                  setEcoPage(1);
                  clearTimeout((window as unknown as Record<string, unknown>)._ecoFilterTimer as ReturnType<typeof setTimeout>);
                  (window as unknown as Record<string, unknown>)._ecoFilterTimer = setTimeout(() => setEcoBrandFilterDebounced(e.target.value), 350);
                }}
                placeholder="filter by brand…"
                className="w-48"
              />
              <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={ecoUnmappedOnly}
                  onChange={(e) => { setEcoUnmappedOnly(e.target.checked); setEcoPage(1); }}
                  className="accent-amber-500"
                />
                Brand not mapped
              </label>
              {ecoResults.isFetching && <span className="text-xs text-slate-400">loading…</span>}
            </div>
            {ecoResults.data && (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="py-2 pr-3">Code</th>
                        <th className="pr-3">Description</th>
                        <th className="pr-3">Brand</th>
                        <th className="pr-3">Ecologic</th>
                        <th className="pr-3">Economic brand?</th>
                        <th>Auto-categories</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ecoResults.data.map((r) => (
                        <tr key={r.productCode} className={!r.brandFound ? "bg-amber-50" : ""}>
                          <td className="py-1.5 pr-3 font-mono text-xs text-slate-500">{r.productCode}</td>
                          <td className="pr-3 max-w-xs truncate text-slate-700">{r.description || <span className="text-slate-300">—</span>}</td>
                          <td className="pr-3 text-slate-600">{r.brand || <span className="text-slate-300">(none)</span>}</td>
                          <td className="pr-3">
                            {r.ecologic == null
                              ? <span className="text-slate-300 text-xs">—</span>
                              : r.ecologic
                                ? <Badge color="green">yes</Badge>
                                : <Badge color="gray">no</Badge>}
                          </td>
                          <td className="pr-3">
                            {r.brandFound === false
                              ? <span className="text-amber-600 text-xs">brand not mapped</span>
                              : r.isEconomic
                                ? <Badge color="amber">yes</Badge>
                                : <Badge color="gray">no</Badge>}
                          </td>
                          <td className="flex flex-wrap gap-1 py-1.5">
                            {r.categories.map((c) => (
                              <Badge key={c} color={c === "ecologic" ? "green" : c === "illogic" ? "amber" : c === "economic" ? "blue" : "gray"}>{c}</Badge>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <Button variant="secondary" size="sm" disabled={ecoPage <= 1} onClick={() => setEcoPage((p) => p - 1)}>← prev</Button>
                  <span className="text-slate-500">page {ecoPage}</span>
                  <Button variant="secondary" size="sm" disabled={(ecoResults.data?.length ?? 0) < 100} onClick={() => setEcoPage((p) => p + 1)}>next →</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

